  import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle, useMemo, useCallback } from "react";
  import JSZip from "jszip";
  import * as tf from "@tensorflow/tfjs";

  import { Button, Col, Dropdown, DropdownItem, DropdownMenu, DropdownToggle, Popover, PopoverBody, UncontrolledDropdown } from "reactstrap";
  import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
  import {
    faCloudArrowUp,
    faDatabase,
    faDownload,
    faExclamationCircle,
    faFileAlt,
    faFileCirclePlus,
    faFilter,
    faGlobe,
    faNetworkWired,
    faPlay,
    faPlus,
    faSpinner,
    faStream,
    faTimesCircle,
    faUpload,
    faVideo,
    faWandMagicSparkles,
  } from "@fortawesome/free-solid-svg-icons";


  import { getCokkie } from "../../login/Login";
  import { Upload } from "@aws-sdk/lib-storage";
  import { CopyObjectCommand, GetObjectCommand, PutObjectCommand, S3Client } from "@aws-sdk/client-s3";
  import Select from "react-select";
  import getDataFromApi from "../../../components/getDataFromApi";
  import { AddDatasetIcon, ChipsetIcon, ChipsetIcon2, CloseIcon, DatasetIcon, DeviceIcon, DownArrowIcon, HistoryIcon, NetworkIcon, PlatformIcon, PlusIcon, SendMessageIcon, UploadIcon, VideoIcon } from "../../Common/SvgIcons/WorkflowIcons";

  import { components } from "react-select";
  import VirtualImageGrid from "./VirtualImageGrid";
  import PCCameraStream from "./PCCameraStream";
import VideoStreaming from "../data/add_data/VideoStreaming";
import EvaluationTargetHardwareMenu from "./EvaluationTargetHardwareMenu";

const LIVE_EVALUATION_ENABLED = false;

  const getDeviceStatusColor = (status) => {
    const normalizedStatus = String(status || "").trim().toLowerCase();
    if (normalizedStatus === "active" || normalizedStatus === "online") {
      return "#28a745";
    }
    if (["inactive", "offline", "off"].includes(normalizedStatus)) {
      return "#dc3545";
    }
    return "#f5c518";
  };

  const DeviceStatusLabel = ({ label, status }) => (
    <div className="d-flex align-items-center gap-2" title={status || "Reserved"}>
      <span
        aria-hidden="true"
        style={{
          width: 8,
          height: 8,
          flex: "0 0 8px",
          borderRadius: "50%",
          backgroundColor: getDeviceStatusColor(status),
          display: "inline-block",
        }}
      />
      <span>{label}</span>
    </div>
  );

  const DeviceOption = (props) => {
    const { label, status } = props.data;

    return (
      <components.Option {...props}>
        <DeviceStatusLabel label={label} status={status} />
      </components.Option>
    );
  };

  const DeviceSingleValue = (props) => (
    <components.SingleValue {...props}>
      <DeviceStatusLabel label={props.data.label} status={props.data.status} />
    </components.SingleValue>
  );

  const AnnotationDropdownIndicator = (indicatorProps) => (
    <components.DropdownIndicator {...indicatorProps}>
      <DownArrowIcon className="cv-annotation-format-select-chevron" />
    </components.DropdownIndicator>
  );

  const ANNOTATION_SELECT_COMPONENTS = {
    DropdownIndicator: AnnotationDropdownIndicator,
  };

  const REGION = "us-east-2";
  const PUBLIC_URL = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
  const getSuggestedImageOptions = (cloudFrontEndpoint, modelName, imageType = "") => {
    const endpoint = String(cloudFrontEndpoint || "").trim().replace(/\/+$/, "");
    const normalizedModelName = String(modelName || "").trim();
    const normalizedImageType = String(imageType || "").trim();

    if (!normalizedModelName) return [];
    // Face-recognition samples have separate reference/test collections and
    // must continue to come from the model-specific endpoint.
    if (!endpoint && normalizedImageType) return [];

    return Array.from({ length: 3 }, (_, index) => {
      const imageNumber = index + 1;
      const imagePrefix = normalizedImageType
        ? `${normalizedModelName}_${normalizedImageType}`
        : normalizedModelName;
      const imageName = `${imagePrefix}_${imageNumber}.jpg`;
      const imageDirectory = normalizedImageType
        ? `${normalizedModelName}/${normalizedImageType}`
        : normalizedModelName;

      return {
        src: endpoint
          ? `${endpoint}/sample_examples/${imageDirectory}/${imageName}`
          : `${PUBLIC_URL}/ico/sample_image_${imageNumber}.jpg`,
        name: imageName,
        label: `${normalizedModelName} sample ${imageNumber}`,
      };
    });
  };

  const NSFW_MODEL_URL = `${PUBLIC_URL}/models/nsfw/mobilenet_v2/`;
  const NSFW_IMAGE_SIZE = 224;
  const NSFW_CLASSES = ["Drawing", "Hentai", "Neutral", "Porn", "Sexy"];
  const NSFW_EXPLICIT_THRESHOLD = 0.25;
  const NSFW_SEXY_THRESHOLD = 0.6;
  const NSFW_SEXY_WITH_EXPLICIT_THRESHOLD = 0.4;
  const NSFW_ADULT_SUM_THRESHOLD = 0.55;
  const PREVIEW_MAX_SIZE = 256;
  const SEGMENTATION_IMAGE_UPLOAD_MAX_SIZE = 640;
  const ZIP_PROCESSING_YIELD_EVERY = 4;
  const RESIZABLE_CANVAS_MIME_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);

  const waitForBrowserPaint = () => new Promise((resolve) => setTimeout(resolve, 0));

  const preloadImageForPreview = (src) =>
    new Promise((resolve) => {
      const image = new Image();
      let settled = false;

      const finish = () => {
        if (settled) return;
        settled = true;

        if (image.naturalWidth && typeof image.decode === "function") {
          image.decode().catch(() => {}).finally(resolve);
          return;
        }

        resolve();
      };

      image.decoding = "async";
      image.onload = finish;
      image.onerror = finish;
      image.src = src;

      if (image.complete) finish();
    });

  const readImageDimensionsFromHeader = async (file) => {
    const headerSize = Math.min(file.size || 0, 256 * 1024);
    if (!headerSize) return null;

    const bytes = new Uint8Array(await file.slice(0, headerSize).arrayBuffer());
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);

    if (
      bytes.length >= 24 &&
      bytes[0] === 0x89 &&
      bytes[1] === 0x50 &&
      bytes[2] === 0x4e &&
      bytes[3] === 0x47
    ) {
      return {
        width: view.getUint32(16),
        height: view.getUint32(20),
      };
    }

    if (bytes.length >= 10 && bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46) {
      return {
        width: view.getUint16(6, true),
        height: view.getUint16(8, true),
      };
    }

    if (bytes.length >= 26 && bytes[0] === 0x42 && bytes[1] === 0x4d) {
      return {
        width: Math.abs(view.getInt32(18, true)),
        height: Math.abs(view.getInt32(22, true)),
      };
    }

    if (bytes.length >= 12 && bytes[0] === 0xff && bytes[1] === 0xd8) {
      let offset = 2;

      while (offset + 9 < bytes.length) {
        if (bytes[offset] !== 0xff) {
          offset += 1;
          continue;
        }

        const marker = bytes[offset + 1];
        offset += 2;

        if (marker === 0xd9 || marker === 0xda) break;
        if (offset + 2 > bytes.length) break;

        const segmentLength = view.getUint16(offset);
        if (segmentLength < 2) break;

        const isStartOfFrame =
          (marker >= 0xc0 && marker <= 0xc3) ||
          (marker >= 0xc5 && marker <= 0xc7) ||
          (marker >= 0xc9 && marker <= 0xcb) ||
          (marker >= 0xcd && marker <= 0xcf);

        if (isStartOfFrame && offset + 7 <= bytes.length) {
          return {
            width: view.getUint16(offset + 5),
            height: view.getUint16(offset + 3),
          };
        }

        offset += segmentLength;
      }
    }

    if (
      bytes.length >= 30 &&
      bytes[0] === 0x52 &&
      bytes[1] === 0x49 &&
      bytes[2] === 0x46 &&
      bytes[3] === 0x46 &&
      bytes[8] === 0x57 &&
      bytes[9] === 0x45 &&
      bytes[10] === 0x42 &&
      bytes[11] === 0x50
    ) {
      const chunk = String.fromCharCode(bytes[12], bytes[13], bytes[14], bytes[15]);

      if (chunk === "VP8X" && bytes.length >= 30) {
        const width = 1 + bytes[24] + (bytes[25] << 8) + (bytes[26] << 16);
        const height = 1 + bytes[27] + (bytes[28] << 8) + (bytes[29] << 16);
        return { width, height };
      }

      if (chunk === "VP8 " && bytes.length >= 30) {
        const width = view.getUint16(26, true) & 0x3fff;
        const height = view.getUint16(28, true) & 0x3fff;
        return { width, height };
      }

      if (chunk === "VP8L" && bytes.length >= 25) {
        const bits =
          (bytes[21] |
            (bytes[22] << 8) |
            (bytes[23] << 16) |
            (bytes[24] << 24)) >>> 0;
        const width = (bits & 0x3fff) + 1;
        const height = ((bits >>> 14) & 0x3fff) + 1;
        return { width, height };
      }
    }

    return null;
  };

  const getBoundedImageSize = (dimensions, maxSize) => {
    if (!dimensions?.width || !dimensions?.height) {
      return { width: maxSize, height: maxSize };
    }

    const scale = Math.min(maxSize / dimensions.width, maxSize / dimensions.height, 1);
    return {
      width: Math.max(1, Math.round(dimensions.width * scale)),
      height: Math.max(1, Math.round(dimensions.height * scale)),
    };
  };

  const getImageResizeTarget = (dimensions, maxSize) => {
    if (!dimensions?.width || !dimensions?.height) return null;

    const longestSide = Math.max(dimensions.width, dimensions.height);
    if (longestSide <= maxSize) return null;

    const scale = maxSize / longestSide;

    return {
      width: Math.max(1, Math.round(dimensions.width * scale)),
      height: Math.max(1, Math.round(dimensions.height * scale)),
    };
  };

  const getCanvasOutputType = (file) => {
    const type = String(file?.type || "").toLowerCase();
    if (type === "image/jpg") return "image/jpeg";
    if (RESIZABLE_CANVAS_MIME_TYPES.has(type)) return type;

    const name = String(file?.name || "");
    if (/\.jpe?g$/i.test(name)) return "image/jpeg";
    if (/\.webp$/i.test(name)) return "image/webp";

    return "image/png";
  };

  const canvasToBlob = (canvas, mimeType) =>
    new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
            return;
          }

          reject(new Error("Failed to resize image."));
        },
        mimeType,
        mimeType === "image/jpeg" || mimeType === "image/webp" ? 0.92 : undefined
      );
    });

  const drawImageToUploadFile = async (file, imageSource, width, height) => {
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext("2d");
    ctx.drawImage(imageSource, 0, 0, width, height);

    const outputType = getCanvasOutputType(file);
    const blob = await canvasToBlob(canvas, outputType);

    return new File([blob], file.name, {
      type: blob.type || outputType,
      lastModified: file.lastModified,
    });
  };

  const resizeImageFileToMaxDimension = async (file, maxSize) => {
    const headerDimensions = await readImageDimensionsFromHeader(file).catch(() => null);
    const headerTarget = getImageResizeTarget(headerDimensions, maxSize);

    if (headerDimensions && !headerTarget) return file;

    if (typeof createImageBitmap === "function") {
      try {
        const bitmap = headerTarget
          ? await createImageBitmap(file, {
              resizeWidth: headerTarget.width,
              resizeHeight: headerTarget.height,
              resizeQuality: "high",
            })
          : await createImageBitmap(file);

        try {
          const bitmapTarget = getImageResizeTarget(
            { width: bitmap.width, height: bitmap.height },
            maxSize
          );
          if (!bitmapTarget && !headerTarget) return file;

          return drawImageToUploadFile(
            file,
            bitmap,
            bitmapTarget?.width || bitmap.width,
            bitmapTarget?.height || bitmap.height
          );
        } finally {
          bitmap.close?.();
        }
      } catch (error) {
        console.warn("Falling back to image element resize", error);
      }
    }

    return new Promise((resolve, reject) => {
      const img = new Image();
      const sourceUrl = URL.createObjectURL(file);

      img.onload = async () => {
        try {
          const target = getImageResizeTarget(
            { width: img.naturalWidth || img.width, height: img.naturalHeight || img.height },
            maxSize
          );

          if (!target) {
            resolve(file);
            return;
          }

          resolve(await drawImageToUploadFile(file, img, target.width, target.height));
        } catch (error) {
          reject(error);
        } finally {
          URL.revokeObjectURL(sourceUrl);
        }
      };

      img.onerror = () => {
        URL.revokeObjectURL(sourceUrl);
        reject(new Error(`Failed to resize image ${file.name}.`));
      };

      img.src = sourceUrl;
    });
  };

  const getNsfwModelJsonUrl = (modelUrl) =>
    modelUrl.endsWith("model.json") ? modelUrl : `${modelUrl.replace(/\/$/, "")}/model.json`;

  const getTopNsfwPredictions = async (logits, topK = 5) => {
    const values = await logits.data();

    return Array.from(values)
      .map((probability, index) => ({
        className: NSFW_CLASSES[index],
        probability,
      }))
      .sort((a, b) => b.probability - a.probability)
      .slice(0, topK);
  };

  const loadNsfwModel = async (modelUrl) => {
    const model = await tf.loadLayersModel(getNsfwModelJsonUrl(modelUrl));
    const warmup = tf.tidy(() =>
      model.predict(tf.zeros([1, NSFW_IMAGE_SIZE, NSFW_IMAGE_SIZE, 3]))
    );
    const warmupTensors = Array.isArray(warmup) ? warmup : [warmup];

    await Promise.all(warmupTensors.map((tensor) => tensor.data()));
    warmupTensors.forEach((tensor) => tensor.dispose());

    return {
      classify: async (img, topK = 5) => {
        const logits = tf.tidy(() => {
          const imageTensor = img instanceof tf.Tensor ? img : tf.browser.fromPixels(img);
          const normalized = imageTensor.toFloat().div(255);
          const resized =
            imageTensor.shape[0] === NSFW_IMAGE_SIZE && imageTensor.shape[1] === NSFW_IMAGE_SIZE
              ? normalized
              : tf.image.resizeBilinear(normalized, [NSFW_IMAGE_SIZE, NSFW_IMAGE_SIZE], true);
          const batched = resized.reshape([1, NSFW_IMAGE_SIZE, NSFW_IMAGE_SIZE, 3]);
          const prediction = model.predict(batched);

          return Array.isArray(prediction) ? prediction[0] : prediction;
        });

        try {
          return await getTopNsfwPredictions(logits, topK);
        } finally {
          logits.dispose();
        }
      },
      dispose: () => model.dispose(),
    };
  };

  const TOOL_CONFIG = {
    image: {
      icon: <UploadIcon className='wf-icon' />,
      title: "Local PC ",
      description: "Upload images or ZIP files",
    },
    s3: {
      icon: <AddDatasetIcon className='wf-icon' />,
      title: "Cloud Transfer (S3)",
      description: "Connect an AWS S3 source",
    },
    stream: {
      icon: <VideoIcon className='wf-icon' />,
      title: "Live Stream",
      description: "Use a PC camera or device stream",
    },
    network: {
      icon: <NetworkIcon className='wf-icon' />,
      title: "Network Transfer",
      description: "Connect a network data source",
    },
    dataset: {
      icon: <PlatformIcon className='wf-icon' />,
      title: "Platform Import",
      description: "Use data already available on DeepEdge",

    },
    "upload-dataset": {
      icon: <DatasetIcon className='wf-icon' />,
      title: "Dataset",
      description: "Upload an annotated dataset ZIP",
    },
    'image-text': {
      icon: <UploadIcon className='wf-icon' />,
      title: "Image + Text",
      description: "Upload paired image and text data",
    },
  };




  const formatOptionLabel = ({ label, type }) => {
    const displayLabel = type === "chipset" ? label.toUpperCase() : label;

    return (
      <div style={{ display: "flex", alignItems: "center" }}>
        <span>{displayLabel}</span>
      </div>
    )
  }

  const toOptions = (obj = {}, isChipset = false) =>
    Object.keys(obj).map((key) => ({
      value: key,
      label: key,
      ...(isChipset ? { type: "chipset" } : {}),
    }));

  const toArrayOptions = (arr = []) =>
    arr.map((item) => ({ value: item, label: item }));


  const CvEvaluationField = ({
    data,
    initialDataSource = "",
    initialSuggestedImages = [],
    hideTestDataSelection = false,
    onEvaluationCreated,
    loader,
    setCallStart,
    table_loading,
    evalStatus,
    onSelectionChange,
    onProgress,
    onComplete,
    chipset,
    compression,
    setOpenEvalDrawer,
    loadButton,
    setLoadButton,
    deviceStatusMap = {},
    memRemainingMB,
    embedded = false,
    onActiveSourceChange,
  }) => {

    // const [selectedFamily, setSelectedFamily] = useState(window.ENV.CUSTOMER === "ambarella" ? "ambarella" : "");
    const [selectedTargetHardware, setSelectedTargetHardware] = useState("edge_device");
    const [selectedFamily, setSelectedFamily] = useState('ambarella');
    // Fresh evaluations should only preselect a chipset when it was explicitly
    // provided through the dashboard URL. Evaluation history must not leak into
    // a new form.
    const [selectedChipset, setSelectedChipset] = useState(chipset || null);
    const [selectedCompression, setSelectedCompression] = useState(compression || null);
    const [selectedDevice, setSelectedDevice] = useState(null);
    const [selectedImages, setSelectedImages] = useState([]);
    const [streamSnapshots, setStreamSnapshots] = useState([]);
    const [selectedImagefiles, setSelectedImagefiles] = useState([]);
    const [selectedrefernceImage, setSelectedrefernceImage] = useState([]);
    const [selectedrefernceImagefiles, setSelectedrefernceImagefiles] = useState([]);
    const [selectedrefernceZip, setSelectedrefernceZip] = useState([]);
    const [selectedZips, setSelectedZips] = useState([]);
    const [selectingSuggestedImage, setSelectingSuggestedImage] = useState("");
    const suggestedImageSelectionInFlightRef = useRef(false);
    const suggestedImageSelectionIdsRef = useRef(new Map());
    const [promptText, setPromptText] = useState("");
    const [dragOver, setDragOver] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadComplete, setUploadComplete] = useState(false);
    const [imageUpload, setImageUpload] = useState(data?.info.task_type === "FaceRecognition" ? false : true);
    const [selectedTool, setSelectedTool] = useState(
      initialDataSource || (embedded ? "image" : "")
    );
    const [s3Url, setS3Url] = useState("");
    const [privateS3, setPrivateS3] = useState("public");
    const [s3BucketName, setS3BucketName] = useState("");
    const [s3Credentials, setS3Credentials] = useState({
      accessKey: '',
      secretKey: '',
      roleId: '',
      role: '',
      region: '',
      s3bucket: '',
      s3url: '',
    });
    const [presigneds3url, setpresigneds3url] = useState("");
    const [references3Url, setreferenceS3Url] = useState("");
    const [referenceprivateS3, setreferencePrivateS3] = useState("public");
    const [references3BucketName, setreferenceS3BucketName] = useState("");
    const [references3Credentials, setreferenceS3Credentials] = useState({
      accessKey: '',
      secretKey: '',
      roleId: '',
      role: '',
      region: '',
      s3bucket: '',
      s3url: '',
    });
    const [referencepresigneds3url, setreferencepresigneds3url] = useState("");

    const [networkfields, setnetworkfields] = useState({
      networkhost: '',
      networkport: '',
      networkuser: '',
      networkpassword: '',
      networkpath: '',

    })

    const [referencenetworkfields, setreferencenetworkfields] = useState({
      networkhost: '',
      networkport: '',
      networkuser: '',
      networkpassword: '',
      networkpath: '',

    })

    const [isDownloading, setIsDownloading] = useState(false);

    const [startstopurl, setStartStopUrl] = useState();
    const [references3file, setreferences3file] = useState(
      data?.info.task_type === "FaceRecognition"
        ? initialSuggestedImages[0]?.target !== "test"
        : false
    );
    const [referencenetworkfile, setreferencenetworkfile] = useState(false);
    const [startStopstatus, setStartStopstatus] = useState({ start: null, stop: null });
    const [rawdata, setrawdata] = useState(true);
    const [selectedDatasetId, setSelectedDatasetId] = useState(null);
    const [jsonFile, setJsonFile] = useState(null);
    const evalStatusRef = useRef({
      status: evalStatus?.status,
      evalId: evalStatus?.evalId,
    });

    const [, setCanclear] = useState(true);
    // console.log(evalStatus);

    const childRef = useRef();
    const infoPopoverTimeoutRef = useRef(null);

    // console.log(data)

    const fileInputRef = useRef(null);
    const fileInputRef2 = useRef(null);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const toggleDropdown = () => setDropdownOpen((prev) => !prev);
    const [format, setFormat] = useState('COCO');
    const [datatype, setDatatype] = useState('Images');
    const isSuperpointKeyPointDetection =
      String(data?.info?.name || "").trim().toLowerCase() === "superpoint" &&
      String(data?.info?.task_type || "").trim().toLowerCase() === "keypoint detection";
    const isFsrcnnImageEnhancement =
      String(data?.info?.name || "").trim().toLowerCase() === "fsrcnn" &&
      String(data?.info?.task_type || "").trim().toLowerCase() === "image enhancement";
    const isDatasetUploadDisabled =
      data?.info?.task_type === "Depth Estimation" ||
      data?.info?.name === "RTMDet" ||
      isSuperpointKeyPointDetection ||
      isFsrcnnImageEnhancement;
    const suggestedImageOptions = useMemo(
      () =>
        getSuggestedImageOptions(
          data?.cloudfront_details?.aws_cf_endpoint,
          data?.info?.name,
          data?.info?.task_type === "FaceRecognition"
            ? references3file
              ? "reference"
              : "test"
            : ""
        ),
      [
        data?.cloudfront_details?.aws_cf_endpoint,
        data?.info?.name,
        data?.info?.task_type,
        references3file,
      ]
    );
    const [stream_type, setStreamtype] = useState('PC_CAMERA');
    const [webrtcUrl, setWebrtcUrl] = useState("");
    const [activeWebrtcUrl, setActiveWebrtcUrl] = useState("");
    const [webrtcUrlError, setWebrtcUrlError] = useState("");
    const [isCooldown, setIsCooldown] = useState(false);
    const [evaluationUploads, setEvaluationUploads] = useState({});
    const [evaluationUploadsReferencedata, setEvaluationUploadsReferencedata] = useState({});
    const [isProcessingZip, setIsProcessingZip] = useState(false);
    const [isProcessingFiles, setIsProcessingFiles] = useState(false);
    const mediaProcessingCountRef = useRef(0);
    const nsfwModelRef = useRef(null);
    const nsfwModelPromiseRef = useRef(null);
    const nsfwChecksInFlightRef = useRef(0);
    const nsfwCheckIdleTimerRef = useRef(null);
    const [nsfwWarning2, setNsfwWarning2] = useState("");
    const [isCheckingNsfw, setIsCheckingNsfw] = useState(false);
    const [flaggedTestImages, setFlaggedTestImages] = useState([]);
    const [flaggedReferenceImages, setFlaggedReferenceImages] = useState([]);
    const nsfwWarning =
      flaggedTestImages.length > 0 || flaggedReferenceImages.length > 0;

    const [infoPopover, setInfoPopover] = useState({
      open: false,
      target: null,
      message: "",
      type: "info",
    });

    const [guideActive, setGuideActive] = useState(false);

    const chipsetRef = useRef(null);
    const familyRef = useRef(null);
    const compressionRef = useRef(null);
    const dataUploadRef = useRef(null);
    const startButtonRef = useRef(null);
    const pcCameraStreamRef = useRef(null);
    const formScrollRef = useRef(null);
    const shouldAutoScrollRef = useRef(true);
    const referenceDropzoneRef = useRef(null);
    const testDropzoneRef = useRef(null);
    const [xmlFiles, setXmlFiles] = useState([]);
    const [annotationFiles, setAnnotationFiles] = useState([]);

    const getActiveTestImages = useCallback(() => {
      if (selectedTool === "stream" && stream_type === "PC_CAMERA") {
        return streamSnapshots;
      }
      return selectedImages;
    }, [selectedImages, selectedTool, streamSnapshots, stream_type]);

    const beginMediaProcessing = useCallback(() => {
      mediaProcessingCountRef.current += 1;
      setIsProcessingFiles(true);
    }, []);

    const endMediaProcessing = useCallback(() => {
      mediaProcessingCountRef.current = Math.max(mediaProcessingCountRef.current - 1, 0);
      if (mediaProcessingCountRef.current === 0) {
        setIsProcessingFiles(false);
      }
    }, []);

    const hasPendingMediaProcessing = useCallback(
      () =>
        mediaProcessingCountRef.current > 0 ||
        suggestedImageSelectionInFlightRef.current ||
        isDownloading ||
        isProcessingFiles ||
        isProcessingZip,
      [isDownloading, isProcessingFiles, isProcessingZip]
    );


    useEffect(() => {
      if (nsfwModelRef.current) return;
      let isMounted = true;

      nsfwModelPromiseRef.current = loadNsfwModel(NSFW_MODEL_URL)
        .then((model) => {
          if (isMounted) {
            nsfwModelRef.current = model;
            return model;
          }

          model.dispose();
          return null;
        })
        .catch((err) => {
          console.error("NSFW model failed to load", err);
          return null;
        });

      return () => {
        isMounted = false;
        if (nsfwCheckIdleTimerRef.current) {
          clearTimeout(nsfwCheckIdleTimerRef.current);
        }
        nsfwModelRef.current?.dispose();
        nsfwModelRef.current = null;
        nsfwModelPromiseRef.current = null;
      };
    }, []);


    useEffect(() => {
      if (!guideActive) return;

      const missing = getNextMissingStep();

      if (missing) {
        showMissingStep(missing);
      } else {
        // 🎯 All required steps done → highlight Start button
        highlightSection(startButtonRef);
        setGuideActive(false);
      }
    }, [
      guideActive,
      selectedTargetHardware,
      selectedChipset,
      selectedCompression,
      selectedImages.length,
      selectedZips.length,
      selectedTool
    ]);

    // useEffect(() => {
    //   if (flaggedImages.length === 0) {
    //     setNsfwWarning(false);
    //   } else {
    //     setNsfwWarning(true);
    //   }
    // }, [flaggedImages]);

    const highlightSection = (ref) => {
      if (!ref?.current) return;

      const container = formScrollRef.current;
      const target = ref.current;

      if (embedded && target) {
        target.scrollIntoView({ behavior: "smooth", block: "center" });
      } else if (container && target) {
        const containerRect = container.getBoundingClientRect();
        const targetRect = target.getBoundingClientRect();
        const targetTop = targetRect.top - containerRect.top + container.scrollTop;

        container.scrollTo({
          top: Math.max(targetTop - 12, 0),
          behavior: "smooth",
        });
      }

      ref.current.classList.add("highlight-required");

      setTimeout(() => {
        ref.current.classList.remove("highlight-required");
      }, 3500);
    };

    const getNextMissingStep = () => {
      const isFaceRec = data?.info?.task_type === "FaceRecognition";

      // 1️⃣ Hardware family, chipset, and available compression are required.
      if (selectedTargetHardware === "edge_device" && !selectedFamily) {
        return { ref: familyRef, message: "Please select a hardware family" };
      }

      if (selectedTargetHardware === "edge_device" && !String(selectedChipset || "").trim()) {
        return { ref: chipsetRef, message: "Please select a chipset" };
      }

      // Check if selected chipset exists in dropdown options
      const chipsetExists =
        selectedTargetHardware === "Host" ||
        chipsetOptions.some((chip) => chip.value === selectedChipset);

      if (selectedTargetHardware === "edge_device" && !chipsetExists) {
        return {
          ref: chipsetRef,
          message: `Selected chipset "${selectedChipset}" is not available in the device farm".`,
        };
      }

      if (
        selectedTargetHardware === "edge_device" &&
        compressionOptions.length > 0 &&
        (selectedCompression === null || selectedCompression === "")
      ) {
        return { ref: compressionRef, message: "Please select a compression" };
      }

      const compressionExists =
        compressionOptions.length === 0 ||
        compressionOptions.some(
          (option) => String(option.value) === String(selectedCompression)
        );

      if (selectedTargetHardware === "edge_device" && !compressionExists) {
        return {
          ref: compressionRef,
          message: `Selected compression "${selectedCompression}" is not available.`,
        };
      }

      // 2️⃣ Tool must be selected
      if (!selectedTool) {
        return { ref: dataUploadRef, message: "Please select a data source" };
      }

      if (!LIVE_EVALUATION_ENABLED && selectedTool === "stream") {
        return {
          ref: dataUploadRef,
          message: "Live evaluation is temporarily unavailable. Please select another data source.",
        };
      }

      if (datatype === "Dataset" && selectedTool === "stream") {
        return {
          ref: dataUploadRef,
          message: "Live data is not supported in Dataset mode. Please select another data source.",
        };
      }

      if (datatype === "Dataset") {
        if (!format) {
          return {
            ref: dataUploadRef,
            message: "Please select an annotation format for the dataset.",
          };
        }

        if (!annotationFormatConfig.enabled.includes(format)) {
          return {
            ref: dataUploadRef,
            message: "Please select a valid annotation format for the dataset.",
          };
        }
      }

      // 3️⃣ Local PC (Image upload)
      if (selectedTool === "image") {
        // ✅ Reference required for FaceRecognition
        if (isFaceRec && (selectedrefernceImage.length === 0 && selectedrefernceZip.length === 0)) {
          return {
            ref: dataUploadRef,
            message: datatype === "Dataset"
              ? "Please upload reference dataset zip files"
              : "Please upload reference image, video, or zip files"
          };
        }

        // ✅ Test required (message differs for FaceRecognition)
        if (selectedImages.length === 0 && selectedZips.length === 0) {
          return {
            ref: dataUploadRef,
            message: isFaceRec
              ? datatype === "Dataset"
                ? "Please upload Test dataset zip files"
                : "Please upload Test image, video, or zip files"
              : datatype === "Dataset"
                ? "Please upload dataset zip files"
                : "Please upload image, video, or zip files"

          };
        }


        if (datatype === "Dataset") {
          const t = data?.info?.task_type;

          if (format === "COCO" && annotationFiles.length === 0) {
            return {
              ref: dataUploadRef,
              message: `No .json annotation file found. Please upload a ${t} dataset zip that contains a .json file.`,
            };
          }

          if (format === "YOLO" && annotationFiles.length === 0) {
            return {
              ref: dataUploadRef,
              message: `No .txt annotation files found. Please upload a YOLO dataset zip that contains annotation .txt files.`,
            };
          }

          if (format === "VOC" && annotationFiles.length === 0) {
            return {
              ref: dataUploadRef,
              message: `No .xml annotation files found. Please upload a VOC dataset zip that contains annotation .xml files.`,
            };
          }

          if (t === "Face Landmark Detection" && annotationFiles.length === 0) {
            return {
              ref: dataUploadRef,
              message: "No .txt annotation file found. Please upload a Face Landmark Detection dataset zip that contains a .txt file.",
            };
          }

          if (t === "Face Landmark Detection" && annotationFiles.length > 1) {
            return {
              ref: dataUploadRef,
              message:
                "Multiple .txt annotation files found. Please upload a Face Landmark Detection dataset zip that contains only one .txt annotation file.",
            };
          }

        }
      }

      if (selectedTool === "stream") {
        if (stream_type === "WebRTC" && !webrtcUrl.trim()) {
          return { ref: dataUploadRef, message: "Please provide a WebRTC URL" };
        }

        if (stream_type === "WebRTC" && !activeWebrtcUrl) {
          return { ref: dataUploadRef, message: "Please start the WebRTC stream before starting evaluation" };
        }

        if (stream_type === "PC_CAMERA" && getActiveTestImages().length === 0) {
          return { ref: dataUploadRef, message: "Please take at least one snapshot before starting evaluation" };
        }
      }

      // 4️⃣ S3
      if (selectedTool === "s3") {
        // ✅ Reference required for FaceRecognition (S3 reference inputs)
        if (isFaceRec) {
          if (referenceprivateS3 === "public") {
            if (!references3BucketName || !references3Url) {
              return { ref: dataUploadRef, message: "Please provide Reference S3 bucket and URL" };
            }
          }

          if (referenceprivateS3 === "presigned") {
            if (!referencepresigneds3url) {
              return { ref: dataUploadRef, message: "Please provide a Reference presigned S3 URL" };
            }
          }

          if (referenceprivateS3 === "iamuser") {
            const {
              accessKey,
              secretKey,
              role,
              roleId,
              region,
              s3bucket,
              s3url,
            } = references3Credentials;

            if (!accessKey || !secretKey || !role || !roleId || !region || !s3bucket || !s3url) {
              return { ref: dataUploadRef, message: "Please fill all Reference IAM S3 credentials" };
            }
          }
        }

        // ✅ Test S3 inputs (existing logic)
        if (privateS3 === "public") {
          if (!s3BucketName || !s3Url) {
            return { ref: dataUploadRef, message: "Please provide S3 bucket and URL" };
          }
        }

        if (privateS3 === "presigned") {
          if (!presigneds3url) {
            return { ref: dataUploadRef, message: "Please provide a presigned S3 URL" };
          }
        }

        if (privateS3 === "iamuser") {
          const {
            accessKey,
            secretKey,
            role,
            roleId,
            region,
            s3bucket,
            s3url,
          } = s3Credentials;

          if (!accessKey || !secretKey || !role || !roleId || !region || !s3bucket || !s3url) {
            return { ref: dataUploadRef, message: "Please fill all IAM S3 credentials" };
          }
        }
      }

      // 5️⃣ Network
      if (selectedTool === "network") {
        const { networkhost, networkport, networkuser, networkpassword, networkpath } = networkfields;

        if (!networkhost || !networkport || !networkuser || !networkpassword || !networkpath) {
          return { ref: dataUploadRef, message: "Please fill all network transfer details" };
        }

        // ✅ Reference required for FaceRecognition (network reference inputs)
        if (isFaceRec) {
          const {
            networkhost: rHost,
            networkport: rPort,
            networkuser: rUser,
            networkpassword: rPass,
            networkpath: rPath,
          } = referencenetworkfields;

          if (!rHost || !rPort || !rUser || !rPass || !rPath) {
            return { ref: dataUploadRef, message: "Please fill all reference network transfer details" };
          }
        }
      }

      // 7️⃣ Dataset
      if (selectedTool === "dataset") {
        if (!selectedDatasetId) {
          return { ref: dataUploadRef, message: "Please select a dataset" };
        }
      }

      // ✅ Everything required is filled
      return null;
    };

    const showMissingStep = (missing) => {
      if (!missing) return;

      highlightSection(missing.ref);

      const isHardwareStep = [familyRef, chipsetRef, compressionRef].includes(missing.ref);
      const fallbackTarget = isHardwareStep
        ? embedded
          ? "cv-embedded-target-hardware-trigger"
          : "cv-target-hardware-trigger"
        : "data-upload-section";
      const targetId = missing.ref?.current?.id || fallbackTarget;

      showInfoPopover(targetId, missing.message, 2500, "danger");
    };






    const showInfoPopover = (targetId, message, timeout = 2000, type = "info") => {
      if (infoPopoverTimeoutRef.current) {
        clearTimeout(infoPopoverTimeoutRef.current);
        infoPopoverTimeoutRef.current = null;
      }

      setInfoPopover({
        open: true,
        target: targetId,
        message,
        type,
      });

      infoPopoverTimeoutRef.current = setTimeout(() => {
        setInfoPopover({ open: false, target: null, message: "", type: "info" });
        infoPopoverTimeoutRef.current = null;
      }, timeout);
    };




    const hideInfoPopover = () => {
      if (infoPopoverTimeoutRef.current) {
        clearTimeout(infoPopoverTimeoutRef.current);
        infoPopoverTimeoutRef.current = null;
      }
      setInfoPopover({ open: false, target: null, message: "" });
    };


    const revokeMediaUrls = useCallback((items = []) => {
      const urls = new Set();

      items.forEach((item) => {
        if (typeof item?.url === "string" && item.url.startsWith("blob:")) {
          urls.add(item.url);
        }
      });

      urls.forEach((url) => URL.revokeObjectURL(url));
    }, []);

    const clearTestMedia = useCallback(() => {
      setSelectedImages((prev) => {
        revokeMediaUrls(prev);
        return [];
      });
      setSelectedImagefiles([]);
      setSelectedZips([]);
      setFlaggedTestImages([]);
      setJsonFile(null);
      setAnnotationFiles([]);
    }, [revokeMediaUrls]);

    const clearReferenceMedia = useCallback(() => {
      setSelectedrefernceImage((prev) => {
        revokeMediaUrls(prev);
        return [];
      });
      setSelectedrefernceImagefiles([]);
      setSelectedrefernceZip([]);
      setFlaggedReferenceImages([]);
    }, [revokeMediaUrls]);

    const selectedImagesRef = useRef(selectedImages);
    const selectedReferenceImagesRef = useRef(selectedrefernceImage);

    useEffect(() => {
      selectedImagesRef.current = selectedImages;
    }, [selectedImages]);

    useEffect(() => {
      selectedReferenceImagesRef.current = selectedrefernceImage;
    }, [selectedrefernceImage]);

    useEffect(() => {
      return () => {
        revokeMediaUrls(selectedImagesRef.current);
        revokeMediaUrls(selectedReferenceImagesRef.current);
      };
    }, [revokeMediaUrls]);


    const handleDragOver = (e) => {
      e.preventDefault();
      setDragOver(true);
    };

    const handleDragLeave = (e) => {
      e.preventDefault();
      setDragOver(false);
    };

    const handleFileDrop = (e) => {
      e.preventDefault();
      setDragOver(false);
      handleFileChange({ target: { files: e.dataTransfer.files, value: "" } });
    };

    const handlereferenceFileDrop = (e) => {
      e.preventDefault();
      setDragOver(false);
      const files = e.dataTransfer.files;
      processreferenceFiles(files);
    };

    const MAX_FILE_SIZE = 1024 * 1024 * 1024;

    // const handleFileChange = (e) => {
    //   const files = Array.from(e.target.files);

    //   if (!files.length) return;

    //   const validFiles = [];
    //   const rejectedFiles = [];

    //   files.forEach((file) => {
    //     if (file.size > MAX_FILE_SIZE) {
    //       rejectedFiles.push(file.name);
    //     } else {
    //       validFiles.push(file);
    //     }
    //   });

    //   if (rejectedFiles.length > 0) {
    //     alert(
    //       `These files exceed the 1 GB limit:\n\n${rejectedFiles.join("\n")}`
    //     );
    //   }

    //   if (validFiles.length > 0) {
    //     processFiles(validFiles);
    //   }

    //   // allow selecting the same file again
    //   e.target.value = "";
    // };

    const handleFileChange = (e) => {
      const files = Array.from(e.target.files);
      if (!files.length) return;

      if (datatype === "Dataset" && !format) {
        showInfoPopover(
          "data-upload-section",
          "Please select an annotation format before uploading the dataset.",
          3000,
          "danger"
        );
        e.target.value = "";
        return;
      }

      const MAX_FILE_SIZE = 1024 * 1024 * 1024;

      const allowedMode = getAllowedKindForUI(); // "Media" | "Dataset"

      const validFiles = [];
      const tooBig = [];
      const rejected = [];

      for (const file of files) {
        if (file.size > MAX_FILE_SIZE) {
          tooBig.push(file.name);
          continue;
        }

        // Dataset accepts ZIPs only. Video uploads are intentionally disabled.
        if (allowedMode === "Dataset") {
          if (!isZip(file)) rejected.push(file.name);
          else validFiles.push(file);
          continue;
        }

        if (isImage(file) || isZip(file)) {
          validFiles.push(file);
        } else {
          rejected.push(file.name);
        }
      }

      if (tooBig.length) {
        showInfoPopover(
          "data-upload-section",
          `These files exceed the 1 GB limit:\n\n${tooBig.join("\n")}`,
          3500,
          "danger"
        );
      }

      if (rejected.length) {
        const msg =
          allowedMode === "Dataset"
            ? "Only .zip files are allowed for Dataset."
            : "Only image or .zip files are allowed for Media.";

        rejectWithPopover(rejected, msg);
      }

      if (validFiles.length) {
        processFiles(validFiles);
      }

      e.target.value = "";
    };

    useEffect(() => {
      evalStatusRef.current = {
        status: evalStatus?.status,
        evalId: evalStatus?.evalId,
      };
      // console.log("in useEffect", evalStatusRef.current);
    }, [evalStatus]);

    // const handleS3ZipProcessing = async (s3url) => {
    //   try {
    //     if (!s3url) {
    //       alert("Please provide a valid S3 or CloudFront URL.");
    //       return;
    //     }

    //     setIsDownloading(true); // ⬅️ Start spinner

    //     const response = await fetch(s3url);

    //     if (!response.ok) {
    //       throw new Error("Failed to fetch file from S3/CloudFront URL");
    //     }

    //     const contentType = response.headers.get("Content-Type");
    //     const blob = await response.blob(); // ⬅️ Spinner shows until this completes

    //     // ZIP
    //     if (s3url.endsWith(".zip") || contentType === "application/zip" || s3url.includes(".zip")) {
    //       const file = new File([blob], "s3-uploaded.zip", { type: "application/zip" });
    //       await processFiles([file]);
    //     }

    //     // Image
    //     else if (
    //       /\.(png|jpe?g|bmp|gif|webp)$/i.test(s3url) ||
    //       s3url.includes("X-Amz-Algorithm") ||
    //       s3url.includes("X-Amz-Credential")
    //     ) {
    //       const extension = s3url.split(".").pop().split(/\#|\?/)[0];
    //       const fileType = contentType || `image/${extension}`;
    //       const fileName = s3url.split("/").pop().split("?")[0];
    //       const file = new File([blob], fileName, { type: fileType });
    //       await previewImage(file);
    //     }

    //     else {
    //       alert("Only .zip files or image URLs are supported.");
    //     }
    //   } catch (err) {
    //     console.error("S3 URL processing error:", err);
    //     alert("Failed to process file from URL: " + err.message);
    //   } finally {
    //     setIsDownloading(false); // ⬅️ Stop spinner
    //   }
    // };


    const customStyles = {
      control: (p) => ({ ...p, backgroundColor: "transparent", border: "1px solid var(--glass-color)", fontSize: '90%', padding: '0px' }),
      dropdownIndicator: (base) => ({
        ...base,
        padding: "0px",
        fontSize: '2px'
      }),

      indicatorSeparator: () => ({
        display: "none",
      }),

      menu: (p) => ({ ...p, backgroundColor: "var(--bg-color8)", zIndex: 9999, fontSize: '90%', border: '2px solid var(--glass-color)' }),
      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
      option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected
          ? "var(--leading-color, #3755d3)"
          : state.isFocused
            ? "var(--card-color)"
            : "transparent",

        color: state.isSelected ? "white" : "var(--text-color)",
        cursor: "pointer",

        "&:active": {
          backgroundColor: "var(--leading-color, #3755d3)",
          color: "white",
        },
      }),

    };


    const scrollDropzoneToBottom = useCallback((el) => {
      if (!shouldAutoScrollRef.current) return;
      if (!el) return;

      // Wait for layout + images
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          el.scrollTop = el.scrollHeight;
        });
      });
    }, []);

    useEffect(() => {
      scrollDropzoneToBottom(testDropzoneRef.current);
    }, [selectedImages.length, selectedZips.length, scrollDropzoneToBottom]);

    useEffect(() => {
      scrollDropzoneToBottom(referenceDropzoneRef.current);
    }, [selectedrefernceImagefiles.length, selectedrefernceZip.length, scrollDropzoneToBottom]);




    const chipsetOptions = useMemo(() => {
      if (!selectedFamily || !data?.devices?.[selectedFamily]) return [];

      return Object.keys(data.devices[selectedFamily]).map((chip) => ({
        label: chip.toUpperCase(),
        value: chip,
      }));
    }, [selectedFamily, data]);

    const compressionOptions = useMemo(() => {
      if (!Array.isArray(data?.compression)) return [];

      return toArrayOptions(
        data.compression.filter((item) => item !== null && item !== undefined && item !== "")
      ).map((option) => ({
        ...option,
        label: String(option.label).replace(/_/g, " "),
      }));
    }, [data?.compression]);

    useEffect(() => {
      setSelectedCompression((current) => {
        const urlCompression = compressionOptions.find(
          (option) =>
            compression &&
            String(option.value).toLowerCase() === String(compression).toLowerCase()
        );

        if (urlCompression) return urlCompression.value;

        return compressionOptions.some((option) => option.value === current) ? current : null;
      });
    }, [compression, compressionOptions]);



    const deviceOptions = useMemo(() => {
      if (!selectedFamily || !selectedChipset) return [];
      // console.log("devicestatusmap", deviceStatusMap)

      return (data?.devices?.[selectedFamily]?.[selectedChipset] || []).map(
        (dev) => ({
          label: dev,
          value: dev,
          status: deviceStatusMap?.[dev]
        })
      );
    }, [selectedFamily, selectedChipset, data, deviceStatusMap]);

    const getDeviceStatus = useCallback(
      (device) => deviceStatusMap?.[device] || "Reserved",
      [deviceStatusMap]
    );

    const annotationFormatConfig = useMemo(() => {
      const taskType = String(data?.info?.task_type || "")
        .trim()
        .toLowerCase()
        .replace(/[_-]+/g, " ")
        .replace(/\s+/g, " ");

      if (["object detection", "zero shot detection", "zeroshot detection"].includes(taskType)) {
        return {
          options: ["COCO", "VOC", "YOLO", "CNTK"],
          defaultValue: "COCO",
          enabled: ["COCO", "VOC", "YOLO"],
        };
      }

      if (["face landmark detection", "face detection"].includes(taskType)) {
        return {
          options: ["WIDER FACE"],
          defaultValue: "WIDER FACE",
          enabled: ["WIDER FACE"],
        };
      }

      if ([
        "classification",
        "image classification",
        "zero shot classification",
        "zeroshot classification",
        "facerecognition",
        "face recognition",
      ].includes(taskType)) {
        return {
          options: ["Named Folder"],
          defaultValue: "Named Folder",
          enabled: ["Named Folder"],
        };
      }

      if (["segmentation", "semantic segmentation"].includes(taskType)) {
        return {
          options: ["Mask", "COCO"],
          defaultValue: "Mask",
          enabled: ["Mask"],
        };
      }

      if (["pose estimation", "keypoint detection"].includes(taskType)) {
        return {
          options: ["COCO"],
          defaultValue: "COCO",
          enabled: ["COCO"],
        };
      }

      return {
        options: ["COCO"],
        defaultValue: "COCO",
        enabled: ["COCO"],
      };
    }, [data?.info?.task_type]);

    const annotationFormatOptions = useMemo(
      () =>
        annotationFormatConfig.options.map((item) => ({
          value: item,
          label: item,
          isDisabled: !annotationFormatConfig.enabled.includes(item),
        })),
      [annotationFormatConfig]
    );

    useEffect(() => {
      if (annotationFormatConfig?.defaultValue) {
        setFormat(annotationFormatConfig.defaultValue);
      }
    }, [annotationFormatConfig]);



    const handleS3ZipProcessing = async (s3url) => {
      let didBeginMediaProcessing = false;

      try {
        if (privateS3 === "iamuser") {
          const { accessKey, secretKey, region, s3bucket } = s3Credentials;

          if (!accessKey || !secretKey || !region || !s3bucket) {
            alert("Please fill all IAM user credentials and bucket details.");
            return;
          }

          beginMediaProcessing();
          didBeginMediaProcessing = true;
          setIsDownloading(true);

          // Extract the key if full S3 URL is given
          let key = s3url;
          if (s3url.startsWith("http")) {
            const parts = new URL(s3url);
            key = parts.pathname.startsWith("/") ? parts.pathname.slice(1) : parts.pathname;
          }

          // Create S3 client
          const s3Client = new S3Client({
            region,
            credentials: {
              accessKeyId: accessKey,
              secretAccessKey: secretKey,
            },
          });

          const command = new GetObjectCommand({
            Bucket: s3bucket,
            Key: key,
          });

          const response = await s3Client.send(command);

          const blob = await response.Body.transformToByteArray();
          const fileType = response.ContentType || "application/octet-stream";

          // Detect and process like before
          if (key.endsWith(".zip") || fileType === "application/zip") {
            const file = new File([blob], "s3-uploaded.zip", { type: "application/zip" });
            await processFiles([file]);
          } else if (/\.(png|jpe?g|bmp|gif|webp)$/i.test(key)) {
            const file = new File([blob], key.split("/").pop(), { type: fileType });
            await previewImage(file);
          } else {
            alert("Only .zip or image files are supported.");
          }
        }

        // Keep your existing logic for public and presigned
        else {
          if (!s3url) {
            alert("Please provide a valid S3 or CloudFront URL.");
            return;
          }

          beginMediaProcessing();
          didBeginMediaProcessing = true;
          setIsDownloading(true); // ⬅️ Start spinner

          const response = await fetch(s3url);

          if (!response.ok) {
            throw new Error("Failed to fetch file from S3/CloudFront URL");
          }

          const contentType = response.headers.get("Content-Type");
          const blob = await response.blob(); // ⬅️ Spinner shows until this completes

          // ZIP
          if (s3url.endsWith(".zip") || contentType === "application/zip" || s3url.includes(".zip")) {
            const file = new File([blob], "s3-uploaded.zip", { type: "application/zip" });
            await processFiles([file]);
          }

          // Image
          else if (
            /\.(png|jpe?g|bmp|gif|webp)$/i.test(s3url) ||
            s3url.includes("X-Amz-Algorithm") ||
            s3url.includes("X-Amz-Credential")
          ) {
            const extension = s3url.split(".").pop().split(/\#|\?/)[0];
            const fileType = contentType || `image/${extension}`;
            const fileName = s3url.split("/").pop().split("?")[0];
            const file = new File([blob], fileName, { type: fileType });
            await previewImage(file);
          }

          else {
            alert("Only .zip files or image URLs are supported.");
          }
        }
      } catch (err) {
        console.error("IAM S3 access error:", err);
        alert("Failed to fetch file" + err.message);
      } finally {
        setIsDownloading(false);
        if (didBeginMediaProcessing) {
          endMediaProcessing();
        }
      }
    };

    // const processFiles = async (files) => {
    //   const fileArray = Array.from(files);
    //   const globalExistingNames = new Set(selectedImages.map(img => img.name));
    //   setIsProcessingZip(true); 
    //   for (const file of fileArray) {
    //     // ✅ Handle image
    //     if (file.type?.startsWith("image/")) {
    //       const uniqueName = makeUniqueFileName(file.name, globalExistingNames);
    //       const renamedFile = new File([file], uniqueName, { type: file.type });
    //       await previewImage(renamedFile);
    //       globalExistingNames.add(uniqueName);
    //     }

    //     // ✅ Handle video
    //     else if (file.type?.startsWith("video/")) {
    //       const uniqueName = makeUniqueFileName(file.name, globalExistingNames);
    //       const renamedFile = new File([file], uniqueName, { type: file.type });
    //       await previewVideo(renamedFile);
    //       globalExistingNames.add(uniqueName);
    //     }

    //     // ✅ Handle ZIP
    //     else if (file.type === "application/zip" || file.name.endsWith(".zip")) {
    //       setIsProcessingZip(true); // 🔹 Start loading

    //       try {

    //       const arrayBuffer = await file.arrayBuffer();
    //       const unzipped = unzipSync(new Uint8Array(arrayBuffer));

    //       const imageEntries = Object.entries(unzipped).filter(([filename]) =>
    //         /\.(png|jpe?g|bmp|gif|webp)$/i.test(filename)
    //       );

    //       const videoEntries = Object.entries(unzipped).filter(([filename]) =>
    //         /\.(mp4|avi|mov|webm)$/i.test(filename)
    //       );

    //       const jsonEntry = Object.entries(unzipped).find(([filename]) =>
    //         /\.json$/i.test(filename)
    //       );

    //       // Handle images inside zip
    //       for (const [filename, uint8array] of imageEntries) {
    //         const baseName = filename.split('/').pop();
    //         const uniqueName = makeUniqueFileName(baseName, globalExistingNames);
    //         const blob = new Blob([uint8array], { type: getMimeType(filename) });
    //         const imageFile = new File([blob], uniqueName, { type: blob.type });
    //         await previewImage(imageFile);
    //         globalExistingNames.add(uniqueName);
    //       }

    //       // ✅ Handle videos inside zip
    //       for (const [filename, uint8array] of videoEntries) {
    //         const baseName = filename.split('/').pop();
    //         const uniqueName = makeUniqueFileName(baseName, globalExistingNames);
    //         const blob = new Blob([uint8array], { type: getMimeType(filename) });
    //         const videoFile = new File([blob], uniqueName, { type: blob.type });
    //         await previewVideo(videoFile);
    //         globalExistingNames.add(uniqueName);
    //       }

    //       // Handle JSON inside zip
    //       if (jsonEntry) {
    //         const [filename, uint8array] = jsonEntry;
    //         const blob = new Blob([uint8array], { type: "application/json" });
    //         const name = filename.split("/").pop();
    //         const file = new File([blob], name, { type: "application/json" });
    //         setJsonFile(file);
    //       } else {
    //         console.warn("No JSON file found in the zip.");
    //       }
    //     } catch (error) {
    //       console.error("Error unzipping:", error);
    //     } finally {
    //       setIsProcessingZip(false); // 🔹 End loading
    //     }

    //     }

    //     else {
    //       alert("Only image, video, or zip files are allowed.");
    //     }
    //   }
    // };

    const processFiles = async (files) => {
      const fileArray = Array.from(files);
      const globalExistingNames = new Set(selectedImages.map(img => img.name));
      beginMediaProcessing(); // 🔹 Start processing files

      try {
        for (const file of fileArray) {
          // Handle image
          if (file.type?.startsWith("image/")) {
            const uniqueName = makeUniqueFileName(file.name.replace(/\s+/g, "_"), globalExistingNames);
            const renamedFile = new File([file], uniqueName, { type: file.type });
            await previewImage(renamedFile);
            globalExistingNames.add(uniqueName);
          }

          // Handle ZIP
          else if (file.type === "application/zip" || file.name.endsWith(".zip")) {
            setIsProcessingZip(true); // Start processing ZIP
            await handleZipFile(file, globalExistingNames);
          }

          else {
            alert("Only image or zip files are allowed.");
          }
        }
      } catch (error) {
        console.error("Error processing files:", error);
      } finally {
        endMediaProcessing(); // End processing files
      }
    };

    // const handleZipFile = async (file, globalExistingNames) => {
    //   try {
    //     const arrayBuffer = await file.arrayBuffer();
    //     const unzipped = unzipSync(new Uint8Array(arrayBuffer));

    //     const imageEntries = Object.entries(unzipped).filter(([filename]) =>
    //       /\.(png|jpe?g|bmp|gif|webp)$/i.test(filename)
    //     );

    //     const videoEntries = Object.entries(unzipped).filter(([filename]) =>
    //       /\.(mp4|avi|mov|webm)$/i.test(filename)
    //     );

    //     const jsonEntry = Object.entries(unzipped).find(([filename]) =>
    //       /\.(json|txt)$/i.test(filename)
    //     );

    //     // Handle images inside zip
    //     // for (const [filename, uint8array] of imageEntries) {
    //     //   const baseName = filename.split('/').pop().replace(/\s+/g, "_");
    //     //   const uniqueName = makeUniqueFileName(baseName, globalExistingNames);
    //     //   const blob = new Blob([uint8array], { type: getMimeType(filename) });
    //     //   const imageFile = new File([blob], uniqueName, { type: blob.type });
    //     //   await previewImage(imageFile);
    //     //   globalExistingNames.add(uniqueName);
    //     //   await new Promise(res => setTimeout(res, 0));
    //     // }
    //     // Handle images inside zip
    //     for (const [filename, uint8array] of imageEntries) {

    //       // filename example: "0/dog.jpg"
    //       const pathParts = filename.split("/");

    //       // Extract folder name (parent directory)
    //       const folderName = pathParts.length > 1
    //         ? pathParts[pathParts.length - 2]
    //         : "";

    //       // Extract original file name
    //       const originalName = pathParts[pathParts.length - 1].replace(/\s+/g, "_");

    //       // First handle uniqueness
    //       const uniqueName = makeUniqueFileName(originalName, globalExistingNames);

    //       // THEN prepend folder name
    //       const finalName = data?.info?.task_type === "Classification" || data?.info?.task_type === "Segmentation" || data?.info?.task_type === "FaceRecognition"
    //         ? folderName
    //           ? `${folderName}_${uniqueName}`
    //           : uniqueName
    //         : uniqueName;

    //       const blob = new Blob([uint8array], { type: getMimeType(filename) });

    //       const imageFile = new File([blob], finalName, {
    //         type: blob.type,
    //       });

    //       await previewImage(imageFile);

    //       globalExistingNames.add(finalName);

    //       await new Promise(res => setTimeout(res, 0));
    //     }

    //     // Handle videos inside zip
    //     for (const [filename, uint8array] of videoEntries) {
    //       const baseName = filename.split('/').pop().replace(/\s+/g, "_");
    //       const uniqueName = makeUniqueFileName(baseName, globalExistingNames);

    //       const blob = new Blob([uint8array], { type: getMimeType(filename) });
    //       const videoFile = new File([blob], uniqueName, { type: blob.type });

    //       await previewVideo(videoFile); // preview immediately
    //       globalExistingNames.add(uniqueName);

    //       // 🔹 Give React time to update UI
    //       await new Promise(res => setTimeout(res, 0));
    //     }

    //     // Handle JSON inside zip
    //     if (jsonEntry && datatype === "Dataset") {
    //       const [filename, uint8array] = jsonEntry;
    //       const blob = new Blob([uint8array], { type: "application/json" });
    //       const name = filename.split("/").pop();
    //       const file = new File([blob], name, { type: "application/json" });
    //       setJsonFile(file);
    //     } else {
    //       console.warn("No JSON file found in the zip.");
    //     }
    //   } catch (error) {
    //     console.error("Error unzipping:", error);
    //   } finally {
    //     setIsProcessingZip(false); // End processing ZIP
    //   }
    // };

    const handleZipFile = async (file, globalExistingNames) => {
      try {
        const zipContent = await JSZip.loadAsync(file);
        const entries = Object.entries(zipContent.files).filter(
          ([filename, zipEntry]) =>
            !zipEntry.dir &&
            filename &&
            !/(^|\/)__MACOSX\//i.test(filename) &&
            !/(^|\/)\.[^/]+$/.test(filename)
        );

        const zipPathMap = entries.reduce((acc, [filename]) => {
          acc[filename] = true;
          return acc;
        }, {});

        if (datatype === "Dataset") {
          const taskType = data?.info?.task_type;
          const v = validateDatasetZip({ unzipped: zipPathMap, taskType });

          if (!v.ok) {
            showInfoPopover("data-upload-section", v.message, 3500, "danger");
            return;
          }
        }

        const imageEntries = entries.filter(([filename]) => IMG_RE.test(filename));
        const videoEntries = entries.filter(([filename]) => VID_RE.test(filename));

        if (videoEntries.length > 0) {
          showInfoPopover(
            "data-upload-section",
            "Video files are not supported right now. Remove videos from the ZIP and try again.",
            3500,
            "danger"
          );
          return;
        }

        // annotation collection
        let builtAnnotationFiles = [];

        if (datatype === "Dataset") {
          if (format === "COCO") {
            const jsonEntries = entries.filter(([filename]) => /\.json$/i.test(filename));

            for (const [filename, zipEntry] of jsonEntries) {
              const name = filename.split("/").pop().replace(/\s+/g, "_");
              const blob = await zipEntry.async("blob");
              builtAnnotationFiles.push(new File([blob], name, { type: "application/json" }));
            }
          }

          if (format === "YOLO") {
            const txtEntries = entries.filter(([filename]) => /\.txt$/i.test(filename));

            for (const [filename, zipEntry] of txtEntries) {
              const name = filename.split("/").pop().replace(/\s+/g, "_");
              const blob = await zipEntry.async("blob");
              builtAnnotationFiles.push(new File([blob], name, { type: "text/plain" }));
            }
          }

          if (format === "VOC") {
            const xmlEntries = entries.filter(([filename]) => /\.xml$/i.test(filename));

            for (const [filename, zipEntry] of xmlEntries) {
              const name = filename.split("/").pop().replace(/\s+/g, "_");
              const blob = await zipEntry.async("blob");
              builtAnnotationFiles.push(new File([blob], name, { type: "application/xml" }));
            }
          }

          if (format === "WIDER FACE") {
            const txtEntries = entries.filter(([filename]) => /\.txt$/i.test(filename));

            for (const [filename, zipEntry] of txtEntries) {
              const name = filename.split("/").pop().replace(/\s+/g, "_");
              const blob = await zipEntry.async("blob");
              builtAnnotationFiles.push(new File([blob], name, { type: "text/plain" }));
            }
          }

          setAnnotationFiles((prev) => [...prev, ...builtAnnotationFiles]);
        }

        let processedEntryCount = 0;

        for (const [filename, zipEntry] of imageEntries) {
          const pathParts = filename.split("/").filter(Boolean);
          const originalName = pathParts[pathParts.length - 1].replace(/\s+/g, "_");
          const lastFolderName =
            pathParts.length > 1 ? pathParts[pathParts.length - 2].replace(/\s+/g, "_") : "";

          const shouldPrependFolder =
            datatype === "Dataset" &&
            ["Classification", "Segmentation", "FaceRecognition"].includes(data?.info?.task_type);

          const baseName =
            shouldPrependFolder && lastFolderName
              ? `${lastFolderName}_${originalName}`
              : originalName;

          const uniqueName = makeUniqueFileName(baseName, globalExistingNames);

          const blob = await zipEntry.async("blob");
          const imageFile = new File([blob], uniqueName, { type: getMimeType(filename) });

          await previewImage(imageFile);
          globalExistingNames.add(uniqueName);

          processedEntryCount += 1;
          if (processedEntryCount % ZIP_PROCESSING_YIELD_EVERY === 0) {
            await waitForBrowserPaint();
          }
        }

      } catch (error) {
        console.error("Error unzipping:", error);
        showInfoPopover(
          "data-upload-section",
          "Failed to read the zip file. Please try again with a valid zip.",
          3500,
          "danger"
        );
      } finally {
        setIsProcessingZip(false);
      }
    };


    const makeUniqueFileName = (name, existingNames) => {
      const ext = name.split('.').pop();
      const baseName = name.replace(/\.[^/.]+$/, '');
      let counter = 1;
      let newName = name;

      while (existingNames.has(newName)) {
        newName = `${baseName}(${counter}).${ext}`;
        counter++;
      }
      return newName;
    }

    const getMimeType = (filename) => {
      const ext = filename.toLowerCase().split('.').pop();
      switch (ext) {
        case 'jpg':
        case 'jpeg': return 'image/jpeg';
        case 'png': return 'image/png';
        case 'bmp': return 'image/bmp';
        case 'gif': return 'image/gif';
        case 'webp': return 'image/webp';
        case 'mp4': return 'video/mp4';
        case 'avi': return 'video/avi';
        case 'mov': return 'video/quicktime';
        case 'webm': return 'video/webm';
        default: return 'application/octet-stream';
      }
    };

    const isZip = (f) =>
      f?.type === "application/zip" ||
      f?.type === "application/x-zip-compressed" ||
      /\.zip$/i.test(f?.name || "");

    const isImage = (f) => f?.type?.startsWith("image/") || /\.(png|jpe?g|bmp|gif|webp)$/i.test(f?.name || "");

    const isMp4 = (f) => f?.type === "video/mp4" || /\.mp4$/i.test(f?.name || "");

    const getAllowedKindForUI = () => {
      if (datatype === "Dataset") return "Dataset";
      return "Media";
    };

    const rejectWithPopover = (names, msg) => {
      if (!names.length) return;
      showInfoPopover("data-upload-section", `${msg}\n\nRejected:\n${names.slice(0, 8).join("\n")}${names.length > 8 ? "\n..." : ""}`, 3500, "danger");
    };

    const shouldResizeSegmentationUploadImage = (file) =>
      datatype !== "Dataset" &&
      String(data?.info?.task_type || "").toLowerCase() === "segmentation" &&
      isImage(file);

    const prepareImageFileForUpload = async (file) => {
      if (!shouldResizeSegmentationUploadImage(file)) return file;

      try {
        return await resizeImageFileToMaxDimension(file, SEGMENTATION_IMAGE_UPLOAD_MAX_SIZE);
      } catch (error) {
        console.warn("Failed to resize segmentation upload image:", error);
        return file;
      }
    };

    // const previewImage = async (file) => {
    //   const url = URL.createObjectURL(file);

    //   setSelectedImages(prev => [
    //     ...prev,
    //     { url, name: file.name, file },
    //   ]);

    //   checkAdultContent(url, file.name, "test");
    //   // 🔹 Allow React to render
    //   await new Promise(res => setTimeout(res, 0));
    // };

    // ---------- Dataset ZIP Validation Helpers ----------
    const IMG_RE = /\.(png|jpe?g|bmp|gif|webp)$/i;
    const VID_RE = /\.(mp4|avi|mov|webm)$/i;
    const JSON_RE = /\.json$/i;
    const TXT_RE = /\.txt$/i;

    const normalizeTaskType = (t) => (t || "").trim().toLowerCase();

    const getZipPaths = (unzippedObj) => Object.keys(unzippedObj || {});

    // "top-level folder" = first segment before "/"
    const topLevelFolders = (paths) => {
      const set = new Set();
      for (const p of paths) {
        if (!p || p.endsWith("/")) continue;
        const parts = p.split("/").filter(Boolean);
        if (parts.length >= 2) set.add(parts[0]); // folder/file
      }
      return [...set];
    };

    const hasAnyFileMatching = (paths, regex) => paths.some((p) => regex.test(p));

    const getAllDirPrefixes = (paths) => {
      // builds all folder prefixes seen in paths
      // e.g. "sample/1/img.jpg" -> adds "sample", "sample/1"
      const set = new Set();
      for (const p of paths) {
        if (!p || p.endsWith("/")) continue;
        const parts = p.split("/").filter(Boolean);
        for (let i = 1; i < parts.length; i++) {
          set.add(parts.slice(0, i).join("/"));
        }
      }
      return set;
    };

    const listChildFolders = (dirPrefixes, parent) => {
      // returns unique immediate children folder names under `parent`
      // parent="" means root
      const out = new Set();

      for (const full of dirPrefixes) {
        const parts = full.split("/").filter(Boolean);

        if (!parent) {
          // root children = first segment of any dir prefix
          if (parts.length >= 1) out.add(parts[0]);
          continue;
        }

        const parentParts = parent.split("/").filter(Boolean);
        let match = true;
        for (let i = 0; i < parentParts.length; i++) {
          if (parts[i] !== parentParts[i]) {
            match = false;
            break;
          }
        }
        if (!match) continue;

        // immediate child folder name
        if (parts.length === parentParts.length + 1) {
          out.add(parts[parentParts.length]);
        }
      }

      return [...out];
    };

    const isNumericFolderName = (name) => /^\d+$/.test(name);

    const hasImagesUnderFolder = (paths, folderPrefix) => {
      const prefix = folderPrefix.endsWith("/") ? folderPrefix : `${folderPrefix}/`;
      return paths.some((p) => p.startsWith(prefix) && IMG_RE.test(p));
    };

    /**
     * Validates dataset zip structure based on task type.
     * Returns: { ok: boolean, message?: string }
     */
    const validateDatasetZip = ({ unzipped, taskType }) => {
      const task = normalizeTaskType(taskType);
      const paths = getZipPaths(unzipped);

      // Only validate when user selected Dataset (you’ll call this conditionally)
      // Common: must contain at least one image somewhere for dataset-based CV tasks
      const hasImages = hasAnyFileMatching(paths, IMG_RE);
      if (!hasImages) {
        return {
          ok: false,
          message:
            "Invalid dataset zip: no images found. Please include images inside the zip.",
        };
      }

      // Object Detection: must include .json annotation
      if (task === "object detection" || task === "objectdetection" || task === "pose estimation" || task === "keypointdetection") {
        if (format === "COCO" && !hasAnyFileMatching(paths, JSON_RE)) {
          return {
            ok: false,
            message: "Invalid dataset: .json annotation file not found in zip.",
          };
        }

        if (format === "YOLO" && !hasAnyFileMatching(paths, TXT_RE)) {
          return {
            ok: false,
            message: "Invalid dataset: YOLO .txt annotation files not found in zip.",
          };
        }

        if (format === "VOC" && !paths.some((p) => /\.xml$/i.test(p))) {
          return {
            ok: false,
            message: "Invalid dataset: VOC .xml annotation files not found in zip.",
          };
        }

        return { ok: true };
      }

      // Face Landmark Detection: must include .txt annotation
      if (task === "face landmark detection" || task === "facelandmarkdetection") {
        const txtFiles = paths.filter((p) => TXT_RE.test(p));

        if (txtFiles.length === 0) {
          return {
            ok: false,
            message:
              "Invalid Face Landmark Detection dataset: no .txt annotation file found in zip. Please upload a dataset zip that contains exactly one .txt file.",
          };
        }

        if (txtFiles.length > 1) {
          return {
            ok: false,
            message:
              "Invalid Face Landmark Detection dataset: multiple .txt annotation files found in zip. Please upload a dataset zip that contains only one .txt file.",
          };
        }

        return { ok: true };
      }

      // Segmentation: top-level folders must include raw and mask
      // if (task === "segmentation") {
      //   const folders = topLevelFolders(paths).map((f) => f.toLowerCase());
      //   const hasRaw = folders.includes("raw");
      //   const hasMask = folders.includes("mask");

      //   if (!hasRaw || !hasMask) {
      //     return {
      //       ok: false,
      //       message:
      //         'Invalid Segmentation dataset: zip must contain top-level folders named "raw" and "mask". Example: raw/img1.jpg and mask/img1.png',
      //     };
      //   }
      //   return { ok: true };
      // }

      if (task === "segmentation") {
        const hasRaw = paths.some((p) => /(^|\/)raw\//i.test(p));
        const hasMask = paths.some((p) => /(^|\/)mask\//i.test(p));

        if (!hasRaw || !hasMask) {
          return {
            ok: false,
            message:
              'Invalid Segmentation dataset: zip must contain folders named "raw" and "mask". Example: raw/img1.jpg and mask/img1.png',
          };
        }

        return { ok: true };
      }

      // Classification: top-level folders must be numeric (0,1,2...) and contain images
      if (task === "classification") {
        const paths = getZipPaths(unzipped);

        // Build folder prefix set
        const dirPrefixes = getAllDirPrefixes(paths);

        // Candidate class folders either at root OR under a single wrapper folder
        const rootChildren = listChildFolders(dirPrefixes, ""); // e.g. ["sample"] or ["0","1"]
        const rootNumeric = rootChildren.filter(isNumericFolderName);

        // ✅ Case A: numeric folders directly under root
        if (rootNumeric.length > 0) {
          const hasAnyImages = rootNumeric.some((c) => hasImagesUnderFolder(paths, c));
          if (!hasAnyImages) {
            return {
              ok: false,
              message:
                "Invalid Classification dataset: class folders found at root but no images detected inside them.",
            };
          }

          // allow 0..n or 1..n — no need to force 0 specifically
          return { ok: true };
        }

        // ✅ Case B: exactly one wrapper folder at root, and numeric folders under it
        if (rootChildren.length === 1) {
          const wrapper = rootChildren[0]; // e.g. "sample"
          const wrapperChildren = listChildFolders(dirPrefixes, wrapper); // e.g. ["1","2","3"]
          const wrapperNumeric = wrapperChildren.filter(isNumericFolderName);

          if (wrapperNumeric.length === 0) {
            return {
              ok: false,
              message:
                "Invalid Classification dataset: expected numeric class folders under the root folder (e.g., sample/1, sample/2, ...).",
            };
          }

          const hasAnyImages = wrapperNumeric.some((c) =>
            hasImagesUnderFolder(paths, `${wrapper}/${c}`)
          );

          if (!hasAnyImages) {
            return {
              ok: false,
              message:
                "Invalid Classification dataset: numeric class folders found but no images detected inside them.",
            };
          }

          return { ok: true };
        }

        // Otherwise invalid (multiple mixed folders at root, etc.)
        return {
          ok: false,
          message:
            "Invalid Classification dataset: zip must contain class folders named 0,1,2... at root OR inside a single root folder (e.g., sample/1, sample/2...).",
        };
      }

      // Default: no strict validation for other task types
      return { ok: true };
    };

    const createImageElementPreviewUrl = (file, maxSize) =>
      new Promise((resolve, reject) => {
        const img = new Image();
        const sourceUrl = URL.createObjectURL(file);

        img.onload = () => {
          const scale = Math.min(maxSize / img.width, maxSize / img.height, 1);

          const canvas = document.createElement("canvas");
          canvas.width = Math.round(img.width * scale);
          canvas.height = Math.round(img.height * scale);

          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

          canvas.toBlob(
            (blob) => {
              URL.revokeObjectURL(sourceUrl);
              if (!blob) {
                reject(new Error("Failed to create image preview."));
                return;
              }

              resolve(URL.createObjectURL(blob));
            },
            "image/jpeg",
            0.7
          );
        };

        img.onerror = () => {
          URL.revokeObjectURL(sourceUrl);
          reject(new Error(`Failed to load image preview for ${file.name}.`));
        };

        img.src = sourceUrl;
      });

    const createSafePreviewUrl = async (file, maxSize = PREVIEW_MAX_SIZE) => {
      if (typeof createImageBitmap === "function") {
        try {
          const dimensions = await readImageDimensionsFromHeader(file);
          const { width, height } = getBoundedImageSize(dimensions, maxSize);
          const bitmap = await createImageBitmap(file, {
            resizeWidth: width,
            resizeHeight: height,
            resizeQuality: "high",
          });

          try {
            const canvas = document.createElement("canvas");
            canvas.width = bitmap.width;
            canvas.height = bitmap.height;

            const ctx = canvas.getContext("2d");
            ctx.drawImage(bitmap, 0, 0);

            const blob = await new Promise((resolve, reject) => {
              canvas.toBlob(
                (previewBlob) => {
                  if (previewBlob) {
                    resolve(previewBlob);
                    return;
                  }

                  reject(new Error("Failed to create image preview."));
                },
                "image/jpeg",
                0.7
              );
            });

            return URL.createObjectURL(blob);
          } finally {
            bitmap.close?.();
          }
        } catch (error) {
          console.warn("Falling back to image element preview", error);
        }
      }

      return createImageElementPreviewUrl(file, maxSize);
    };


    const previewImage = async (file) => {
      const uploadFile = await prepareImageFileForUpload(file);
      const previewUrl = await createSafePreviewUrl(uploadFile);

      setSelectedImages((prev) => [
        ...prev,
        { url: previewUrl, name: uploadFile.name, file: uploadFile },
      ]);

      await checkAdultContent(previewUrl, uploadFile.name, "test");
      await waitForBrowserPaint();
    };


    const previewVideo = async (file) => {
      return new Promise(async (resolve) => {
        const url = URL.createObjectURL(file); // ✔ much lighter than Base64

        setSelectedImages((prev) => [
          ...prev,
          { url, name: file.name, type: "video", file }
        ]);

        // Optional: If you track files separately


        // 🔹 Let React paint this video preview before processing next one
        await new Promise(res => setTimeout(res, 0));

        resolve();
      });
    };


    const removeFlaggedImages = () => {
      const flaggedTestSet = new Set(flaggedTestImages);
      const flaggedReferenceSet = new Set(flaggedReferenceImages);

      setSelectedImages((prev) => {
        revokeMediaUrls(prev.filter((img) => flaggedTestSet.has(img.name)));
        return prev.filter((img) => !flaggedTestSet.has(img.name));
      });

      setSelectedImagefiles((prev) =>
        prev.filter((img) => !flaggedTestSet.has(img.name))
      );


      setSelectedrefernceImage((prev) => {
        revokeMediaUrls(prev.filter((img) => flaggedReferenceSet.has(img.name)));
        return prev.filter((img) => !flaggedReferenceSet.has(img.name));
      });

      setSelectedrefernceImagefiles((prev) =>
        prev.filter((img) => !flaggedReferenceSet.has(img.name))
      );

      setFlaggedTestImages([]);
      setFlaggedReferenceImages([]);
    };



    const handlereferenceFileChange = (e) => {
      const files = e.target.files;
      if (files.length > 0) {
        processreferenceFiles(files);
      }
      e.target.value = "";
    };

    const handlereferenceS3ZipProcessing = async (s3url) => {
      let didBeginMediaProcessing = false;

      try {
        if (!s3url) {
          alert("Please provide a valid S3 or CloudFront URL.");
          return;
        }

        beginMediaProcessing();
        didBeginMediaProcessing = true;
        setIsDownloading(true); // ⬅️ Start spinner

        const response = await fetch(s3url);

        if (!response.ok) {
          throw new Error("Failed to fetch file from S3/CloudFront URL");
        }

        const contentType = response.headers.get("Content-Type");
        const blob = await response.blob(); // ⬅️ Spinner shows until this completes

        // ZIP
        if (s3url.endsWith(".zip") || contentType === "application/zip" || s3url.includes(".zip")) {
          const file = new File([blob], "s3-uploaded.zip", { type: "application/zip" });
          await processreferenceFiles([file]);
        }

        // Image
        else if (
          /\.(png|jpe?g|bmp|gif|webp)$/i.test(s3url) ||
          s3url.includes("X-Amz-Algorithm") ||
          s3url.includes("X-Amz-Credential")
        ) {
          const extension = s3url.split(".").pop().split(/\#|\?/)[0];
          const fileType = contentType || `image/${extension}`;
          const fileName = s3url.split("/").pop().split("?")[0];
          const file = new File([blob], fileName, { type: fileType });
          await previewreferenceImage(file);
        }

        else {
          alert("Only .zip files or image URLs are supported.");
        }
      } catch (err) {
        console.error("S3 URL processing error:", err);
        alert("Failed to process file from URL: " + err.message);
      } finally {
        setIsDownloading(false); // ⬅️ Stop spinner
        if (didBeginMediaProcessing) {
          endMediaProcessing();
        }
      }
    };




    // --- Virtualization (no libs) ---
    const refThumbWrapRef = useRef(null);
    const [refThumbViewport, setRefThumbViewport] = useState({ width: 0, height: 0, scrollTop: 0 });

    const THUMB_W = 70;  // cell width (includes gap/spacing)
    const THUMB_H = 60;  // cell height (includes gap/spacing)
    const OVERSCAN_ROWS = 3;

    const refRO = useRef(null);

    // Track size with ResizeObserver (Reference)
    useEffect(() => {
      const el = refThumbWrapRef.current;
      if (!el) return;

      // cleanup old observer if effect reruns
      if (refRO.current) refRO.current.disconnect();

      const ro = new ResizeObserver(() => {
        setRefThumbViewport((p) => ({
          ...p,
          width: el.clientWidth,
          height: el.clientHeight,
        }));
      });

      ro.observe(el);
      refRO.current = ro;

      // initial measure
      setRefThumbViewport((p) => ({
        ...p,
        width: el.clientWidth,
        height: el.clientHeight,
        scrollTop: el.scrollTop,
      }));

      return () => ro.disconnect();
    }, [selectedrefernceImagefiles.length]); // ✅ important (was [])


    // Track scrollTop
    const onRefThumbScroll = (e) => {
      const el = e.currentTarget;
      setRefThumbViewport((p) => ({ ...p, scrollTop: el.scrollTop }));
    };




    // --- Virtualization for selectedImages (test) ---
    const testThumbWrapRef = useRef(null);
    const [testThumbViewport, setTestThumbViewport] = useState({ width: 0, height: 0, scrollTop: 0 });

    const TEST_CELL_W = 95;
    const TEST_CELL_H = 75;
    const TEST_OVERSCAN = 3;

    const testRO = useRef(null);

    // Track size with ResizeObserver (Test)
    useEffect(() => {
      const el = testThumbWrapRef.current;
      if (!el) return;

      if (testRO.current) testRO.current.disconnect();

      const ro = new ResizeObserver(() => {
        setTestThumbViewport((p) => ({
          ...p,
          width: el.clientWidth,
          height: el.clientHeight,
        }));
      });

      ro.observe(el);
      testRO.current = ro;

      setTestThumbViewport((p) => ({
        ...p,
        width: el.clientWidth,
        height: el.clientHeight,
        scrollTop: el.scrollTop,
      }));

      return () => ro.disconnect();
    }, [selectedImages.length]); // ✅ important (was [])


    const onTestThumbScroll = (e) => {
      const el = e.currentTarget;
      setTestThumbViewport((p) => ({ ...p, scrollTop: el.scrollTop }));
    };



    // const processreferenceFiles = async (files) => {
    //   const fileArray = Array.from(files);

    //   for (const file of fileArray) {
    //     if (file.type?.startsWith("image/")) {
    //       const existingNames = new Set(selectedrefernceImage.map(img => img.name));
    //       const uniqueName = makeUniqueFileName(file.name, existingNames);
    //       const renamedFile = new File([file], uniqueName, { type: file.type });
    //       await previewreferenceImage(renamedFile);

    //     } else if (file.type === "application/zip" || file.name.endsWith(".zip")) {
    //       const zip = new JSZip();
    //       const zipContent = await zip.loadAsync(file);

    //       for (const filename of Object.keys(zipContent.files)) {
    //         const zipEntry = zipContent.files[filename];

    //         if (!zipEntry.dir && /\.(png|jpe?g|bmp|gif|webp)$/i.test(filename)) {
    //           const fileData = await zipEntry.async("blob");

    //           const baseName = filename.split("/").pop();
    //           const existingNames = new Set(selectedrefernceImage.map(img => img.name));
    //           const uniqueName = makeUniqueFileName(baseName, existingNames);

    //           const imageFile = new File([fileData], uniqueName, { type: getMimeType(filename) });
    //           await previewreferenceImage(imageFile);
    //         }
    //       }
    //     } else {
    //       alert("Only image files or zip files are allowed.");
    //     }
    //   }
    // };

    const processreferenceFiles = async (files) => {
      const fileArray = Array.from(files);
      const existingNames = new Set(selectedrefernceImage.map(img => img.name));

      beginMediaProcessing();

      try {
        for (const file of fileArray) {

          // Handle direct image
          if (file.type?.startsWith("image/")) {
            const uniqueName = makeUniqueFileName(file.name.replace(/\s+/g, "_"), existingNames);

            const renamedFile = new File([file], uniqueName, { type: file.type });
            await previewreferenceImage(renamedFile);
            existingNames.add(uniqueName);

            continue;
          }

          // Handle ZIP
          if (file.type === "application/zip" || file.name.endsWith(".zip")) {
            setIsProcessingZip(true);

            try {
              const zipContent = await JSZip.loadAsync(file);
              const imageEntries = Object.entries(zipContent.files).filter(
                ([filename, zipEntry]) =>
                  !zipEntry.dir &&
                  IMG_RE.test(filename) &&
                  !/(^|\/)__MACOSX\//i.test(filename) &&
                  !/(^|\/)\.[^/]+$/.test(filename)
              );

              for (let i = 0; i < imageEntries.length; i++) {
                const [filename, zipEntry] = imageEntries[i];
                const baseName = filename.split("/").pop().replace(/\s+/g, "_");
                const uniqueName = makeUniqueFileName(baseName, existingNames);
                const fileData = await zipEntry.async("blob");

                const imageFile = new File([fileData], uniqueName, {
                  type: getMimeType(filename),
                });

                await previewreferenceImage(imageFile);
                existingNames.add(uniqueName);

                if ((i + 1) % ZIP_PROCESSING_YIELD_EVERY === 0) {
                  await waitForBrowserPaint();
                }
              }
            } catch (error) {
              console.error("Error unzipping reference images:", error);
              showInfoPopover(
                "data-upload-section",
                "Failed to read the reference zip file. Please try again with a valid zip.",
                3500,
                "danger"
              );
            } finally {
              setIsProcessingZip(false);
            }

            continue;
          }

          alert("Only image files or zip files are allowed.");
        }
      } finally {
        endMediaProcessing();
      }
    };


    // const previewreferenceImage = async (file) => {
    //   return new Promise((resolve) => {
    //     const reader = new FileReader();
    //     reader.onload = (e) => {
    //       const base64 = e.target.result;
    //       setSelectedrefernceImage((prev) => [
    //         ...prev,
    //         { base64, name: file.name }
    //       ]);
    //       setSelectedrefernceImagefiles((prev) => [
    //         ...prev,
    //         { base64, name: file.name }
    //       ]);
    //       resolve();
    //     };
    //     reader.readAsDataURL(file);
    //   });
    // };

    const previewreferenceImage = async (file) => {
      const url = await createSafePreviewUrl(file);

      setSelectedrefernceImage(prev => [
        ...prev,
        { url, name: file.name, file }
      ]);

      setSelectedrefernceImagefiles(prev => [
        ...prev,
        { url, name: file.name, file }
      ]);

      await checkAdultContent(url, file.name, "reference");
      // Let React update preview immediately
      await waitForBrowserPaint();
    };


    // const handleRemovereferenceImage = (index) => {
    //   setSelectedrefernceImage((prev) => prev.filter((_, i) => i !== index));
    //   setSelectedrefernceImagefiles((prev) => prev.filter((_, i) => i !== index));
    // };

    const handleRemovereferenceImage = (idx) => {
      setSelectedrefernceImage((prev) => {
        const item = prev[idx];
        suggestedImageSelectionIdsRef.current.delete(item?.name);
        if (item?.url) URL.revokeObjectURL(item.url);

        setFlaggedReferenceImages((f) =>
          f.filter((name) => name !== item.name)
        );

        return prev.filter((_, i) => i !== idx);
      });

      setSelectedrefernceImagefiles((prev) =>
        prev.filter((_, i) => i !== idx)
      );
    };


    const handleRemoveImage = useCallback((index) => {
      setSelectedImages((prev) => {
        const item = prev[index];
        suggestedImageSelectionIdsRef.current.delete(item?.name);
        if (item?.url) URL.revokeObjectURL(item.url);

        setFlaggedTestImages((f) => f.filter((name) => name !== item.name));
        return prev.filter((_, i) => i !== index);
      });
    }, []);

    const downloadSuggestedImage = async (suggestedImage) => {
      try {
        const response = await fetch(suggestedImage.src);
        if (!response.ok) {
          throw new Error(`Suggested image request failed with status ${response.status}.`);
        }

        return await response.blob();
      } catch (cloudFrontError) {
        const {
          aws_cf_access_key: accessKeyId,
          aws_cf_secret_key: secretAccessKey,
          aws_cf_bucket_name: bucketName,
        } = data?.cloudfront_details || {};

        if (!accessKeyId || !secretAccessKey || !bucketName) {
          throw cloudFrontError;
        }

        const imageUrl = new URL(suggestedImage.src);
        const objectKey = decodeURIComponent(imageUrl.pathname.replace(/^\/+/, ""));
        const s3Client = new S3Client({
          region: REGION,
          credentials: {
            accessKeyId,
            secretAccessKey,
          },
        });
        const response = await s3Client.send(
          new GetObjectCommand({
            Bucket: bucketName,
            Key: objectKey,
          })
        );
        const imageBytes = await response.Body.transformToByteArray();

        return new Blob([imageBytes], {
          type: response.ContentType || "image/jpeg",
        });
      }
    };

    const handleSuggestedImageSelect = async (
      suggestedImage,
      targetOverride = "",
      previewElement = null
    ) => {
      if (embedded && selectedTool !== "image") {
        setSelectedTool("image");
      }

      const isReferenceSelection =
        targetOverride
          ? targetOverride === "reference"
          : data?.info?.task_type === "FaceRecognition" && references3file;
      const activeImages = isReferenceSelection
        ? selectedrefernceImage
        : selectedImages;
      const selectedIndex = activeImages.findIndex(
        (image) => image?.name === suggestedImage.name
      );

      if (selectedIndex !== -1) {
        if (isReferenceSelection) {
          handleRemovereferenceImage(selectedIndex);
        } else {
          handleRemoveImage(selectedIndex);
        }
        return;
      }

      if (suggestedImageSelectionInFlightRef.current) return;

      suggestedImageSelectionInFlightRef.current = true;
      setSelectingSuggestedImage(suggestedImage.name);
      const selectionId = `${suggestedImage.name}:${Date.now()}`;
      suggestedImageSelectionIdsRef.current.set(suggestedImage.name, selectionId);

      const pendingImage = {
        url: suggestedImage.src,
        sourceUrl: suggestedImage.src,
        name: suggestedImage.name,
        isRemoteSample: true,
        isPreparing: true,
        selectionId,
        previewElement,
        file: null,
      };

      if (isReferenceSelection) {
        setSelectedrefernceImage((current) => [...current, pendingImage]);
        setSelectedrefernceImagefiles((current) => [...current, pendingImage]);
      } else {
        setSelectedImages((current) => [...current, pendingImage]);
      }

      try {
        const blob = await downloadSuggestedImage(suggestedImage);
        const downloadedFile = new File([blob], suggestedImage.name, {
          type: blob.type || "image/jpeg",
        });
        const file = isReferenceSelection
          ? downloadedFile
          : await prepareImageFileForUpload(downloadedFile);
        const analysisPreviewUrl = await createSafePreviewUrl(file);
        await preloadImageForPreview(analysisPreviewUrl);
        const preparedImage = {
          url: analysisPreviewUrl,
          name: suggestedImage.name,
          selectionId,
          // Continue displaying the already-painted sample thumbnail so the
          // background file preparation never changes its crop or sizing.
          previewElement,
          file,
        };

        const replacePendingImage = (current) => current.map((image) => {
          if (image?.selectionId !== selectionId || !image?.isPreparing) {
            return image;
          }

          return preparedImage;
        });

        if (isReferenceSelection) {
          setSelectedrefernceImage(replacePendingImage);
          setSelectedrefernceImagefiles(replacePendingImage);
        } else {
          setSelectedImages(replacePendingImage);
        }

        if (suggestedImageSelectionIdsRef.current.get(suggestedImage.name) === selectionId) {
          await checkAdultContent(
            analysisPreviewUrl,
            suggestedImage.name,
            isReferenceSelection ? "reference" : "test"
          );
        } else {
          URL.revokeObjectURL(analysisPreviewUrl);
        }
      } catch (error) {
        console.error("Failed to select suggested image", error);
        const removePendingImage = (current) => current.filter(
          (image) => image?.selectionId !== selectionId || !image?.isPreparing
        );

        if (isReferenceSelection) {
          setSelectedrefernceImage(removePendingImage);
          setSelectedrefernceImagefiles(removePendingImage);
        } else {
          setSelectedImages(removePendingImage);
        }
        showInfoPopover(
          "data-upload-section",
          "Unable to select this image. Please try another image.",
          3000,
          "danger"
        );
      } finally {
        if (suggestedImageSelectionIdsRef.current.get(suggestedImage.name) === selectionId) {
          suggestedImageSelectionIdsRef.current.delete(suggestedImage.name);
        }
        suggestedImageSelectionInFlightRef.current = false;
        setSelectingSuggestedImage("");
      }
    };

    const initialSuggestedImagesLoadedRef = useRef(false);
    useEffect(() => {
      if (
        initialSuggestedImagesLoadedRef.current ||
        selectedTool !== "image" ||
        initialSuggestedImages.length === 0
      ) {
        return;
      }

      initialSuggestedImagesLoadedRef.current = true;
      initialSuggestedImages.forEach((suggestedImage) => {
        const remoteMedia = {
          url: suggestedImage.src,
          sourceUrl: suggestedImage.src,
          name: suggestedImage.name,
          remoteSourceKey: decodeURIComponent(
            new URL(suggestedImage.src).pathname.replace(/^\/+/, "")
          ),
          isRemoteSample: true,
          file: null,
        };

        if (suggestedImage.target === "reference") {
          setSelectedrefernceImage((current) => [...current, remoteMedia]);
          setSelectedrefernceImagefiles((current) => [...current, remoteMedia]);
        } else {
          setSelectedImages((current) => [...current, remoteMedia]);
        }
      });
    }, [initialSuggestedImages, selectedTool]);


    const handleRemovereferenceZip = (index) => {
      setSelectedrefernceZip((prev) => prev.filter((_, i) => i !== index));
    };

    const handleRemoveZip = (index) => {
      setSelectedZips((prev) => prev.filter((_, i) => i !== index));
    };

    const handlerefernceFileSelect = () => {
      if (fileInputRef2.current) fileInputRef2.current.click();
    };

    const handleFileSelect = () => {
      if (fileInputRef.current) fileInputRef.current.click();
    };

    const handleS3Upload = async (file, fileType = "generic") => {
      setUploadComplete(false);
      if (!file) {
        console.error("Missing file or AWS credentials");
        throw new Error("Missing file or AWS credentials");
      }

      let keyName;
      if (file?.name?.endsWith(".zip")) {
        const filename =
          fileType === "reference"
            ? "reference_data.zip"
            : fileType === "test"
              ? "data.zip"
              : "data.zip";
        keyName = `model_hub/${data?.info.name}/data/${filename}`;
      } else {
        keyName = file.name;
      }


      const uploadParams = {
        Bucket: data?.cloud_front_cred.aws_cf_bucket_name,
        Key: keyName,
        Body: file,
      };

      try {
        const s3Client = new S3Client({
          region: REGION,
          credentials: {
            accessKeyId: data?.temp_aws_cred.aws_access_key_id,
            secretAccessKey: data?.temp_aws_cred.aws_secret_access_key,
            sessionToken: data?.temp_aws_cred.aws_session_token,
          },
        });
        const uploader = new Upload({
          client: s3Client,
          leavePartsOnError: false,
          params: uploadParams,
        });
        uploader.on("httpUploadProgress", (progress) => {
          const percentage = Math.round((progress.loaded / progress.total) * 100);
          setUploadProgress(percentage);
        });
        await uploader.done();
        setUploadComplete(true);
        return true;
      } catch (error) {
        console.error("Upload failed for", keyName, ":", error);
        throw error;
      }
    };

    // const uploadToS3 = async (file, evalId) => {
    //   const { aws_cf_access_key, aws_cf_secret_key, aws_cf_bucket_name, aws_cf_endpoint } = data?.cloudfront_details;

    //   const s3 = new S3Client({
    //     region: REGION, // or the actual region of your bucket
    //     credentials: {
    //       accessKeyId: aws_cf_access_key,
    //       secretAccessKey: aws_cf_secret_key,
    //     },

    //   });

    //   const keyName = `evaluations/${evalId}/${file.name}`;

    //   console.log(file);

    //   const uploadParams = {
    //     Bucket: aws_cf_bucket_name,
    //     Key: keyName,
    //     Body: file, // directly upload the File object
    //     ContentType: file.type,
    //   };

    //   await s3.send(new PutObjectCommand(uploadParams));

    //   return keyName; // return the path so you can send to backend
    // };


    // ⬇️ Drop this in place of your current uploadToS3
    const uploadToS3 = async (file, evalId, type) => {
      if (!file || !evalId) {
        throw new Error("Missing file or evalId");
      }

      const {
        aws_cf_access_key,
        aws_cf_secret_key,
        aws_cf_bucket_name,
      } = data?.cloudfront_details || {};

      if (!aws_cf_access_key || !aws_cf_secret_key || !aws_cf_bucket_name) {
        throw new Error("Missing S3 credentials or bucket name");
      }

      // Reset progress/completion UI if you use it elsewhere
      setUploadComplete(false);
      setUploadProgress(0);

      const s3Client = new S3Client({
        region: REGION, // "us-east-2" in your file
        credentials: {
          accessKeyId: aws_cf_access_key,
          secretAccessKey: aws_cf_secret_key,
        },
      });

      const keyName = type ? `evaluations/${evalId}/${type}_${file.name}` : `evaluations/${evalId}/${file.name}`;

      const uploader = new Upload({
        client: s3Client,
        params: {
          Bucket: aws_cf_bucket_name,
          Key: keyName,
          Body: file,
          ContentType: file.type || "application/octet-stream",
        },
        leavePartsOnError: false,
      });

      // (optional) show progress — safe in browser (XHR path)
      uploader.on("httpUploadProgress", (p) => {
        if (p?.loaded) {
          const total = p.total ?? p.loaded; // some envs omit total
          const pct = Math.round((p.loaded / total) * 100);
          if (onProgress) onProgress(pct);
          setUploadProgress(pct);
        }
      });

      await uploader.done();
      if (onComplete) onComplete();
      setUploadComplete(true);

      return keyName; // you'll keep using this to build your CF/S3 URL
    };

    const copySuggestedImageToEvaluation = async (media, evalId, type = "") => {
      if (!media?.remoteSourceKey || !evalId) {
        throw new Error("Missing sample image source or evaluation id");
      }

      const {
        aws_cf_access_key,
        aws_cf_secret_key,
        aws_cf_bucket_name,
      } = data?.cloudfront_details || {};

      if (!aws_cf_access_key || !aws_cf_secret_key || !aws_cf_bucket_name) {
        throw new Error("Missing S3 credentials or bucket name");
      }

      const s3Client = new S3Client({
        region: REGION,
        credentials: {
          accessKeyId: aws_cf_access_key,
          secretAccessKey: aws_cf_secret_key,
        },
      });
      const destinationKey = type
        ? `evaluations/${evalId}/${type}_${media.name}`
        : `evaluations/${evalId}/${media.name}`;
      const sourceParts = media.remoteSourceKey.split("/");
      const sampleModelName = sourceParts[1] || data?.info?.name || "";
      const sourceKeys = Array.from(new Set([
        media.remoteSourceKey.replace(/^hub_sample_images\//, "sample_examples/"),
        sampleModelName
          ? `model_hub/${sampleModelName}/sample_images/${media.name}`
          : "",
        media.remoteSourceKey,
      ].filter(Boolean)));
      let copyError;

      for (const sourceKey of sourceKeys) {
        const encodedSourceKey = sourceKey
          .split("/")
          .map((segment) => encodeURIComponent(segment))
          .join("/");

        try {
          await s3Client.send(
            new CopyObjectCommand({
              Bucket: aws_cf_bucket_name,
              CopySource: `${aws_cf_bucket_name}/${encodedSourceKey}`,
              Key: destinationKey,
            })
          );
          copyError = null;
          break;
        } catch (error) {
          copyError = error;
        }
      }

      if (copyError) throw copyError;

      return destinationKey;
    };



    // const uploadInWorker = ({ evalId, files, modelId, taskType }) => {
    //   const worker = new Worker(
    //     new URL("../../../worker/UploadWorker.js", import.meta.url),
    //     { type: "module" }
    //   );

    //   // Mark upload started (important if user reloads mid-upload)
    //   updateUploadState(evalId, { started: true, progress: 0, complete: false });

    //   worker.postMessage({
    //     evalId,
    //     files,
    //     region: REGION,
    //     bucket: data.cloudfront_details.aws_cf_bucket_name,
    //     credentials: {
    //       accessKeyId: data.cloudfront_details.aws_cf_access_key,
    //       secretAccessKey: data.cloudfront_details.aws_cf_secret_key,
    //     },
    //   });

    //   worker.onmessage = (e) => {
    //     const msg = e.data;

    //     if (msg.type === "progress") {
    //       setEvaluationUploads((prev) => ({
    //         ...prev,
    //         [evalId]: { ...(prev[evalId] || {}), progress: msg.percent },
    //       }));

    //       updateUploadState(evalId, { progress: msg.percent, complete: false });
    //     }

    //     if (msg.type === "done") {
    //       setEvaluationUploads((prev) => ({
    //         ...prev,
    //         [evalId]: { ...(prev[evalId] || {}), progress: 100, complete: true },
    //       }));

    //       updateUploadState(evalId, { progress: 100, complete: true });

    //       setCanclear(true);
    //       worker.terminate();
    //     }

    //     if (msg.type === "error") {
    //       console.error("Upload failed:", msg.error);

    //       updateUploadState(evalId, { error: msg.error, complete: false });

    //       worker.terminate();
    //     }
    //   };
    // };

    const uploadAnnotationFile = async (evalId, file) => {
      const s3Client = new S3Client({
        region: REGION,
        credentials: {
          accessKeyId: data.cloudfront_details.aws_cf_access_key,
          secretAccessKey: data.cloudfront_details.aws_cf_secret_key,
        },
        maxAttempts: 5,
      });

      const key = `evaluations/${evalId}/${file.name}`;

      const uploader = new Upload({
        client: s3Client,
        params: {
          Bucket: data.cloudfront_details.aws_cf_bucket_name,
          Key: key,
          Body: file,
          ContentType: "application/json",
        },
        queueSize: 2,                     // stable parallel parts
        partSize: 10 * 1024 * 1024,       // 10MB parts (safe for huge files)
        leavePartsOnError: false,
      });

      uploader.on("httpUploadProgress", (progress) => {
        if (progress?.loaded && progress?.total) {
          const percent = Math.round(
            (progress.loaded / progress.total) * 100
          );

          setEvaluationUploads((prev) => ({
            ...prev,
            [evalId]: {
              ...(prev[evalId] || {}),
              progress: percent,
            },
          }));
        }
      });

      await uploader.done(); // waits until multipart fully completes
    };

    const uploadInWorker = ({ evalId, files, modelId, taskType }) => {
      const worker = new Worker(
        new URL("../../../worker/UploadWorker.js", import.meta.url),
        { type: "module" }
      );

      updateUploadState(evalId, { started: true, progress: 0, complete: false });

      worker.postMessage({
        evalId,
        files,
        region: REGION,
        bucket: data.cloudfront_details.aws_cf_bucket_name,
        credentials: {
          accessKeyId: data.cloudfront_details.aws_cf_access_key,
          secretAccessKey: data.cloudfront_details.aws_cf_secret_key,
        },
      });

      worker.onmessage = (e) => {
        const msg = e.data;

        if (msg.type === "progress") {
          setEvaluationUploads((prev) => ({
            ...prev,
            [evalId]: { ...(prev[evalId] || {}), progress: msg.percent },
          }));

          updateUploadState(evalId, { progress: msg.percent, complete: false });
          return;
        }

        // ✅ NEW: retries are informational; do NOT terminate worker
        if (msg.type === "retry") {
          updateUploadState(evalId, {
            lastRetry: {
              fileName: msg.fileName,
              attempt: msg.attempt,
              message: msg.message,
            },
          });
          return;
        }

        if (msg.type === "done") {
          setEvaluationUploads((prev) => ({
            ...prev,
            [evalId]: { ...(prev[evalId] || {}), progress: 100, complete: true },
          }));

          updateUploadState(evalId, { progress: 100, complete: true });

          setCanclear(true);
          worker.terminate();
          return;
        }

        // ✅ "error" should now mean FINAL failure (worker only emits it after retries)
        if (msg.type === "error") {
          console.error("Upload failed:", msg.error);

          setEvaluationUploads((prev) => ({
            ...prev,
            [evalId]: { ...(prev[evalId] || {}), error: msg.error, complete: false },
          }));

          updateUploadState(evalId, { error: msg.error, complete: false });

          setCanclear(true); // optional: allow UI actions again
          worker.terminate();
          return;
        }
      };

      worker.onerror = (err) => {
        console.error("Worker runtime error:", err);

        setEvaluationUploads((prev) => ({
          ...prev,
          [evalId]: { ...(prev[evalId] || {}), error: String(err?.message || err), complete: false },
        }));

        updateUploadState(evalId, { error: String(err?.message || err), complete: false });

        setCanclear(true);
        worker.terminate();
      };
    };

      const updateUploadState = (evalId, patch) => {
      const raw = sessionStorage.getItem("evalUploadState");
      const map = raw ? JSON.parse(raw) : {};
      map[evalId] = {
        ...(map[evalId] || {}),
        ...patch,
        updatedAt: Date.now(),
      };
      sessionStorage.setItem("evalUploadState", JSON.stringify(map));
    };

    function base64ToFile(base64String, fileName, mimeType) {
      const byteString = atob(base64String.split(",")[1]);
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);

      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
      }

      // auto-detect mime if not passed
      const ext = fileName.split('.').pop().toLowerCase();
      let finalMime = mimeType;
      if (!mimeType) {
        finalMime = getMimeType(fileName);
      }

      return new File([ab], fileName, { type: finalMime });
    }




    const bytesToMB = (bytes) => (bytes / (1024 * 1024)).toFixed(2);

    const handleSubmitNewEvaluation = async () => {
      const missing = getNextMissingStep();
      if (missing) {
        showMissingStep(missing);
        return;
      }

      const activeTestImages = getActiveTestImages();
      if (
        activeTestImages.some(
          (media) => media?.type === "video" || media?.file?.type?.startsWith("video/")
        )
      ) {
        showInfoPopover(
          "data-upload-section",
          "Video input is not supported right now.",
          3000,
          "danger"
        );
        return;
      }
      const totalUploadBytes = calculateTotalUploadSize();
      const totalUploadMB = bytesToMB(totalUploadBytes);

      if (hasPendingMediaProcessing()) {
        showInfoPopover(
          "data-upload-section",
          "Please wait until the URL download and files finish loading before starting evaluation."
        );
        return;
      }

      if (isCheckingNsfw) {
        showInfoPopover(
          "data-upload-section",
          "Please wait while uploaded images are checked for sensitive content.",
          2500,
          "danger"
        );
        return;
      }

      // if (nsfwWarning) {
      //   showInfoPopover(
      //     "data-upload-section",
      //     "Sensitive or adult content was detected.",
      //     3500,
      //     "danger"
      //   );
      //   return;
      // }

      setSelectedImagefiles([]);
      setSelectedrefernceImagefiles([]);

      // console.log("selectedImages", selectedImages.length);
      // console.log("eval_status", evalStatus?.status);

      if (selectedTargetHardware === "edge_device" && (!selectedFamily || !selectedChipset)) {
        console.error("Both family and chipset must be selected!");
        return;
      }


      setLoadButton(true);
      try {
        let generatedZipFile = null;

        let dataSource = "BrowserUpload";
        if (selectedTool === "s3") {
          dataSource = "s3";
        } else if (selectedTool === "stream") {
          dataSource = "stream";
        } else if (selectedTool === "image") {
          dataSource = "BrowserUpload";
        }

        const payload = {
          target: selectedTargetHardware === "Host" ? "Host" : "Registered_Device",
          device: selectedTargetHardware === "Host" ? "" : selectedDevice ?? "",
          chipset: selectedTargetHardware === "Host" ? "" : selectedChipset ?? "",
          family: selectedTargetHardware === "Host" ? "" : selectedFamily ?? "",
          ...(selectedTargetHardware === "edge_device" &&
            compressionOptions.length > 0 &&
            selectedCompression !== null
            ? { compression: selectedCompression }
            : {}),
          memory_consumed: totalUploadMB,
          ...(["LLM", "VLM"].includes(data?.Screen_type) && { input_prompt: promptText.trim() }),
          total_images:
            data?.info?.task_type !== "FaceRecognition"
              ? activeTestImages.length
              : activeTestImages.length + selectedrefernceImage.length,
          reference_images: selectedrefernceImage.length,
        };

        if (selectedTool === "s3") {
          dataSource = "S3";

          if (privateS3 === "iamuser") {

            payload.roleId = s3Credentials.roleId;
            payload.role = s3Credentials.role;
            payload.accessKey = s3Credentials.accessKey;
            payload.secretKey = s3Credentials.secretKey;
            payload.region = s3Credentials.region;
            payload.s3bucket = s3Credentials.s3bucket;
            payload.s3url = s3Credentials.s3url;


          }
        } else if (selectedTool === "stream") {
          dataSource = "stream";
          payload.data_source = dataSource;
          payload.stream_type = stream_type;
          if (stream_type === "WebRTC") {
            payload.stream_url = activeWebrtcUrl;
          }
        } else if (selectedTool === "network") {
          dataSource = "Network";
          payload.data_source = dataSource;
          payload.network_url = networkfields.networkhost + ":" + networkfields.networkport;
          payload.network_url_user = networkfields.networkuser;
          payload.network_url_password = networkfields.networkpassword;
          payload.data_path = networkfields.networkpath;

        } else if (selectedTool === "image") {
          dataSource = "BrowserUpload";
          payload.data_source = dataSource;
        } else if (selectedTool === "dataset") {
          dataSource = "datasets";
          payload.data_source = dataSource;
          payload.dataset_id = selectedDatasetId;
        }
        if (datatype === "Dataset") {
          payload.is_dataset = true;
          payload.annotation_format = format.toLowerCase();
        }
        payload.input_type = "images";

        const token = getCokkie("jwtToken");
        const url = `${window["ENV"].API_URL}/evaluations?model_id=${data?.info?._id}`;
        const myHeaders = new Headers();
        myHeaders.append("Authorization", `Bearer ${token}`);
        myHeaders.append("Content-Type", "application/json");


        const resultdata = await getDataFromApi(url, "POST", payload);
        setEvaluationUploads(prev => ({
          ...prev,
          [resultdata?.evaluation_id]: {
            images: [...activeTestImages],
            progress: 0,
            complete: false,
            annotationFiles: annotationFiles || [],
          },
        }));
        if (data?.info.task_type === "FaceRecognition" && resultdata) {
          setEvaluationUploadsReferencedata(prev => ({
            ...prev,
            [resultdata?.evaluation_id]: {
              images: [...selectedrefernceImage], // clone to keep separate
              progress: 0,
              complete: false,
            },
          }));
        }

        onEvaluationCreated(
          resultdata,
          selectedTargetHardware === "Host" ? "Host" : selectedChipset,
          activeTestImages?.length,
          selectedTool
        );
        clearTestMedia();
        setStreamSnapshots([]);
        setActiveWebrtcUrl("");
        clearReferenceMedia();

        // 1) If any images were picked, bundle them into data.zip and upload:
        if (resultdata && selectedTool === "network") {
          const payload = {
            _id: resultdata?.evaluation_id,
            action: "network_download",
            network_url: networkfields.networkhost + ":" + networkfields.networkport,
            user: networkfields.networkuser,
            password: networkfields.networkpassword,
            data_path: networkfields.networkpath,
            task_type: data?.info?.task_type,
            model_id: data?.info?._id,
          };

          const token = getCokkie("jwtToken");
          const url = `${window["ENV"].API_URL}/evaluations/action?visual_inspection=false`;
          const myHeaders = new Headers();
          myHeaders.append("Authorization", `Bearer ${token}`);
          myHeaders.append("Content-Type", "application/json");


          const result = await getDataFromApi(url, "POST", payload);

        }


        if (
          (data?.Screen_type === "Computer Vision" || data?.Screen_type === "VLM") &&
          activeTestImages.length > 0
        ) {
          if (data?.info.task_type === "FaceRecognition" && resultdata) {
            handleUploadreferencefile(resultdata, evaluationUploadsReferencedata?.[resultdata?.evaluation_id]?.images);
          }
          else if (activeTestImages.length > 0 && imageUpload) {
            
            const evalAnnotationFiles =
              evaluationUploads[resultdata.evaluation_id]?.annotationFiles || annotationFiles || [];

            if (evalAnnotationFiles.length > 0) {
              for (const annFile of evalAnnotationFiles) {
                await uploadAnnotationFile(resultdata.evaluation_id, annFile);
              }
            }
            const uploadSet =
              evaluationUploads[resultdata?.evaluation_id]?.images || activeTestImages;
            // for (let i = 0; i < uploadSet.length; i++) {
            //   setCanclear(false);
            //   const media = uploadSet[i];
            //   const isFirst = i === 0;
            //   const isLast = i === uploadSet.length - 1;

            //   if (
            //     (evalStatusRef.current?.status === "Terminated" || evalStatusRef.current?.status === "Failed") &&
            //     evalStatusRef.current?.evalId === resultdata?.evaluation_id
            //   ) {
            //     break;
            //   }

            //   // Create proper File object from base64
            //   // const file = base64ToFile(media.base64, media.name);
            //   const file = media.file;  // Access the actual File object

            //   // Upload to S3
            //   const keyName = await uploadToS3(file, resultdata?.evaluation_id);

            //   // Payload differs slightly for images vs videos
            //   const payload = {
            //     _id: resultdata?.evaluation_id,
            //     action: "data_upload",
            //     isFirst,
            //     iscomplete: isLast,
            //     task_type: data?.info?.task_type,
            //     model_id: data?.info?._id,
            //   };

            //   if (media.type === "video") {
            //     payload.video_name = media?.name;
            //     payload.video_url = `https://de-cloudfront-s3.s3.us-east-2.amazonaws.com/${keyName}`;
            //     payload.isVideo = true;
            //   } else {
            //     payload.image_name = media?.name;
            //     payload.image_url = `https://de-cloudfront-s3.s3.us-east-2.amazonaws.com/${keyName}`;
            //   }
            //   const token = getCokkie("jwtToken");
            //   const url = `${window["ENV"].API_URL}/evaluations/action?visual_inspection=false`;
            //   const myHeaders = new Headers();
            //   myHeaders.append("Authorization", `Bearer ${token}`);
            //   myHeaders.append("Content-Type", "application/json");

            //   const response = await fetch(url, {
            //     method: "POST",
            //     headers: myHeaders,
            //     body: JSON.stringify(payload),
            //   });

            //   const result = await response.json();
            // }


            // if (uploadSet.length > 0) {
            //   setCanclear(false);

            //   uploadInWorker({
            //     evalId: resultdata.evaluation_id,
            //     files: uploadSet.map(i => i.file),
            //     modelId: data?.info?._id,
            //     taskType: data?.info?.task_type,
            //   });
            // }

            // let uploadSet = evaluationUploads[resultdata?.evaluation_id]?.images || selectedImages;

            // // ✅ If JSON exists, push it into same upload batch
            // if (!rawdata && jsonFile) {
            //   uploadSet = [              
            //     {
            //       file: jsonFile,
            //       name: jsonFile.name,
            //       type: jsonFile.type || "application/json",
            //     },
            //     ...uploadSet,
            //   ];
            // }

            const remoteSampleImages = uploadSet.filter(
              media => media.isRemoteSample && media.remoteSourceKey
            );

            if (remoteSampleImages.length > 0) {
              await Promise.all(
                remoteSampleImages.map((media) =>
                  copySuggestedImageToEvaluation(media, resultdata.evaluation_id)
                )
              );
            }

            const imageFiles = uploadSet.filter(
              media => media.file && !media.file.type.startsWith("video/")
            );

            const videoFiles = uploadSet.filter(
              media => media.file?.type.startsWith("video/")
            );

            // ================================
            // 1️⃣ IMAGES → WORKER (PARALLEL)
            // ================================
            if (imageFiles.length > 0) {
              setCanclear(false);

              uploadInWorker({
                evalId: resultdata.evaluation_id,
                files: imageFiles.map(i => i.file),
                modelId: data?.info?._id,
                taskType: data?.info?.task_type,
              });
            }

            // ================================
            // 2️⃣ VIDEOS → MAIN THREAD (SAFE)
            // ================================
            if (videoFiles.length > 0) {
              setCanclear(false);

              for (const media of videoFiles) {
                await uploadToS3(
                  media.file,
                  resultdata.evaluation_id,
                  null
                );
              }
            }



            setEvaluationUploads(prev => ({
              ...prev,
              [resultdata?.evaluation_id]: {
                ...prev[resultdata?.evaluation_id],
                progress: 100,
                complete: true,
              },
            }));

            setCanclear(true);
          }
        }


        if (data?.Screen_type === "Computer Vision" || data?.Screen_type === "VLM") {
          clearTestMedia();
          setStreamSnapshots([]);
          clearReferenceMedia();
          // setSelectedChipset(null);
          setSelectedImagefiles([]);
          setSelectedrefernceImagefiles([]);
          setS3Credentials({});
          setpresigneds3url("");
          setS3BucketName("");
          setS3Url("");
          setreferenceS3Url("");
          setS3Credentials({
            accessKey: '',
            secretKey: '',
            roleId: '',
            role: '',
            region: '',
            s3bucket: '',
            s3url: '',
          });
          setreferenceS3Credentials({
            accessKey: '',
            secretKey: '',
            roleId: '',
            role: '',
            region: '',
            s3bucket: '',
            s3url: '',
          });

          setnetworkfields({
            networkhost: '',
            networkport: '',
            networkuser: '',
            networkpassword: '',
            networkpath: '',

          });
          setreferencenetworkfields({
            networkhost: '',
            networkport: '',
            networkuser: '',
            networkpassword: '',
            networkpath: '',

          })
          // Keep the embedded source selected until the parent switches to the
          // newly created evaluation. Clearing it here removes the complete
          // drop-zone body and leaves a blank composer during that transition.
          if (!embedded) {
            setSelectedTool("");
          }
        }

        if (["LLM", "VLM"].includes(data?.Screen_type)) {
          setPromptText("");
        }


        setLoadButton(false);
        setSelectedZips([]);
      } catch (error) {
        setLoadButton(false);
        console.error("Error submitting data:", error);
      }
    };

    // const handleUploadreferencefile = async (resultdata) => {
    //   if (!selectedFamily || !selectedChipset) {
    //     console.error("Both family and chipset must be selected!");
    //     return;
    //   }
    //   for (let i = 0; i < selectedrefernceImage.length; i++) {
    //     setCanclear(false);

    //     if (
    //       (evalStatusRef.current?.status === "Terminated" || evalStatusRef.current?.status === "Failed") &&
    //       evalStatusRef.current?.evalId === resultdata?.evaluation_id
    //     ) {
    //       break;
    //     }

    //     const image = selectedrefernceImage[i];
    //     const isFirst = i === 0;
    //     const isLast = i === selectedrefernceImage.length - 1;

    //     const file = base64ToFile(image.base64, image.name);

    //     const keyName = await uploadToS3(file, resultdata?.evaluation_id);

    //     const payload = {
    //       _id: resultdata?.evaluation_id,
    //       action: "data_upload",
    //       image_name: image.name,
    //       // image: image.base64.split(',')[1],
    //       image_url: `https://de-cloudfront-s3.s3.us-east-2.amazonaws.com/${keyName}`,
    //       isFirst: isFirst,
    //       iscomplete: false,
    //       reference_data: true,
    //       task_type: data?.info?.task_type,
    //       model_id: data?.info?._id,
    //     };

    //     const token = getCokkie("jwtToken");
    //     const url = `${window["ENV"].API_URL}/evaluations/action?visual_inspection=false`;
    //     const myHeaders = new Headers();
    //     myHeaders.append("Authorization", `Bearer ${token}`);
    //     myHeaders.append("Content-Type", "application/json");

    //     const response = await fetch(url, {
    //       method: "POST",
    //       headers: myHeaders,
    //       body: JSON.stringify(payload),
    //     });

    //     const result = await response.json();

    //     if (selectedImages.length > 0 && isLast) {
    //       if (!rawdata && jsonFile) {
    //         const formData = new FormData();
    //         formData.append("_id", resultdata?.evaluation_id);
    //         formData.append("action", "data_upload");
    //         formData.append("file", jsonFile, jsonFile.name);
    //         formData.append("annotation_format", format);
    //         formData.append("task_type", data?.info?.task_type);
    //         formData.append("model_id", data?.info?._id);

    //         const token = getCokkie("jwtToken");
    //         const url = `${window["ENV"].API_URL}/evaluations/upload`;

    //         const response = await fetch(url, {
    //           method: "POST",
    //           headers: {
    //             Authorization: `Bearer ${token}`,

    //           },
    //           body: formData,
    //         });

    //         const result = await response.json();
    //       }
    //       for (let i = 0; i < selectedImages.length; i++) {
    //         setCanclear(false);
    //         const media = selectedImages[i]; // could be image OR video
    //         const isFirst = i === 0;
    //         const isLast = i === selectedImages.length - 1;

    //         if (
    //           (evalStatusRef.current?.status === "Terminated" || evalStatusRef.current?.status === "Failed") &&
    //           evalStatusRef.current?.evalId === resultdata?.evaluation_id
    //         ) {
    //           break;
    //         }

    //         // Create proper File object from base64
    //         const file = base64ToFile(media.base64, media.name);

    //         // Upload to S3
    //         const keyName = await uploadToS3(file, resultdata?.evaluation_id);

    //         // Payload differs slightly for images vs videos
    //         const payload = {
    //           _id: resultdata?.evaluation_id,
    //           action: "data_upload",
    //           // image: media.base64.split(',')[1],
    //           isFirst,
    //           iscomplete: isLast,
    //           task_type: data?.info?.task_type,
    //           model_id: data?.info?._id,
    //         };

    //         if (media.type === "video") {
    //           payload.video_name = media?.name;
    //           payload.video_url = `https://de-cloudfront-s3.s3.us-east-2.amazonaws.com/${keyName}`;
    //           payload.isVideo = true;
    //         } else {
    //           payload.image_name = media?.name;
    //           payload.image_url = `https://de-cloudfront-s3.s3.us-east-2.amazonaws.com/${keyName}`;
    //         }
    //         const token = getCokkie("jwtToken");
    //         const url = `${window["ENV"].API_URL}/evaluations/action?visual_inspection=false`;
    //         const myHeaders = new Headers();
    //         myHeaders.append("Authorization", `Bearer ${token}`);
    //         myHeaders.append("Content-Type", "application/json");

    //         const response = await fetch(url, {
    //           method: "POST",
    //           headers: myHeaders,
    //           body: JSON.stringify(payload),
    //         });

    //         const result = await response.json();

    //       }
    //       setCanclear(true);
    //     }
    //   }
    //   setCanclear(true);


    // }

    const handleUploadreferencefile = async (resultdata) => {
      if (selectedTargetHardware === "edge_device" && (!selectedFamily || !selectedChipset)) {
        console.error("Both family and chipset must be selected!");
        return;
      }

      const token = getCokkie("jwtToken");
      const actionUrl = `${window["ENV"].API_URL}/evaluations/action?visual_inspection=false`;
      const uploadUrl = `${window["ENV"].API_URL}/evaluations/upload`;

      const makeHeaders = () => {
        const myHeaders = new Headers();
        myHeaders.append("Authorization", `Bearer ${token}`);
        myHeaders.append("Content-Type", "application/json");
        return myHeaders;
      };

      const uploadSingleReference = async (image, isFirst, isLast) => {
        setCanclear(false);
        if (
          (evalStatusRef.current?.status === "Terminated") &&
          evalStatusRef.current?.evalId === resultdata?.evaluation_id
        ) {
          return false;
        }

        // const file = base64ToFile(image.base64, image.name);
        const keyName = image.isRemoteSample
          ? await copySuggestedImageToEvaluation(
            image,
            resultdata?.evaluation_id,
            "reference"
          )
          : await uploadToS3(image.file, resultdata?.evaluation_id, "reference");

        // const payload = {
        //   _id: resultdata?.evaluation_id,
        //   action: "data_upload",
        //   image_name: image.name,
        //   image_url: `https://de-cloudfront-s3.s3.us-east-2.amazonaws.com/${keyName}`,
        //   isFirst,
        //   iscomplete: isLast,
        //   reference_data: true,
        //   task_type: data?.info?.task_type,
        //   model_id: data?.info?._id,
        // };

        // const response = await fetch(actionUrl, {
        //   method: "POST",
        //   headers: makeHeaders(),
        //   body: JSON.stringify(payload),
        // });

        // return await response.json();
      };

      const uploadTestImages = async () => {
        const uploadSet =
          evaluationUploads[resultdata?.evaluation_id]?.images || getActiveTestImages();
        if (uploadSet.length > 0) {
          if (!rawdata && jsonFile) {
            const formData = new FormData();
            formData.append("_id", resultdata?.evaluation_id);
            formData.append("action", "data_upload");
            formData.append("file", jsonFile, jsonFile.name);
            formData.append("annotation_format", format);
            formData.append("task_type", data?.info?.task_type);
            formData.append("model_id", data?.info?._id);


            await getDataFromApi(uploadUrl, "POST", formData);
          }

          for (let i = 0; i < uploadSet.length; i++) {
            setCanclear(false);
            const media = uploadSet[i];
            const isFirst = i === 0;
            const isLast = i === uploadSet.length - 1;

            if (
              (evalStatusRef.current?.status === "Terminated" ||
                evalStatusRef.current?.status === "Failed") &&
              evalStatusRef.current?.evalId === resultdata?.evaluation_id
            ) {
              break;
            }

            // const file = base64ToFile(media.base64, media.name);
            const keyName = media.isRemoteSample
              ? await copySuggestedImageToEvaluation(
                media,
                resultdata?.evaluation_id,
                "test"
              )
              : await uploadToS3(media.file, resultdata?.evaluation_id, "test");

            // const payload = {
            //   _id: resultdata?.evaluation_id,
            //   action: "data_upload",
            //   isFirst,
            //   iscomplete: isLast,
            //   task_type: data?.info?.task_type,
            //   model_id: data?.info?._id,
            // };

            // if (media.type === "video") {
            //   payload.video_name = media?.name;
            //   payload.video_url = `https://de-cloudfront-s3.s3.us-east-2.amazonaws.com/${keyName}`;
            //   payload.isVideo = true;
            // } else {
            //   payload.image_name = media?.name;
            //   payload.image_url = `https://de-cloudfront-s3.s3.us-east-2.amazonaws.com/${keyName}`;
            // }

            // await fetch(actionUrl, {
            //   method: "POST",
            //   headers: makeHeaders(),
            //   body: JSON.stringify(payload),
            // });
          }
        }
      };

      const uploadreferenceSet = evaluationUploadsReferencedata[resultdata?.evaluation_id]?.images || selectedrefernceImage;
      if (uploadreferenceSet.length > 0) {
        await uploadSingleReference(uploadreferenceSet[0], true, false);
      }


      await uploadTestImages();


      if (uploadreferenceSet.length > 1) {
        for (let i = 1; i < uploadreferenceSet.length; i++) {
          const isLast = i === uploadreferenceSet.length - 1;
          await uploadSingleReference(uploadreferenceSet[i], false, isLast);
        }
      }

      setCanclear(true);
    }


    const isFamilyChipsetValid =
      selectedTargetHardware === "Host" || (selectedFamily && selectedChipset);
    let isAdditionalValid = true;
    if (data?.Screen_type === "Computer Vision") {
      if (selectedTool === "image") {
        isAdditionalValid = selectedImages.length > 0 || selectedZips.length > 0;
      } else if (selectedTool === "s3") {
        if (privateS3 === "iamuser") {

          isAdditionalValid = s3Credentials.accessKey && s3Credentials.secretKey && s3Credentials.role && s3Credentials.roleId && s3Credentials.region && s3Credentials.s3bucket && s3Credentials.s3url;

        } else if (privateS3 === "presigned") {
          isAdditionalValid = presigneds3url;
        } else if (privateS3 === "public") {
          isAdditionalValid = s3BucketName && s3Url;
        }
        if (data?.info.task_type === "FaceRecognition") {
          if (referenceprivateS3 === "iamuser") {

            isAdditionalValid = isAdditionalValid && (
              references3Credentials.accessKey &&
              references3Credentials.secretKey &&
              references3Credentials.role &&
              references3Credentials.roleId &&
              references3Credentials.region &&
              references3Credentials.s3bucket &&
              references3Credentials.s3url
            );

          } else if (referenceprivateS3 === "presigned") {
            isAdditionalValid = isAdditionalValid && referencepresigneds3url;
          } else if (referenceprivateS3 === "public") {
            isAdditionalValid = isAdditionalValid && (references3BucketName && references3Url);
          }

        }

      } else if (selectedTool === "network") {
        isAdditionalValid = networkfields.networkhost &&
          networkfields.networkport &&
          networkfields.networkuser &&
          networkfields.networkpassword &&
          networkfields.networkpath;

        if (data?.info.task_type === "FaceRecognition") {
          isAdditionalValid = isAdditionalValid && (
            referencenetworkfields.networkhost &&
            referencenetworkfields.networkport &&
            referencenetworkfields.networkuser &&
            referencenetworkfields.networkpassword &&
            referencenetworkfields.networkpath
          );
        }
      } else if (selectedTool === "stream") {
        isAdditionalValid =
          stream_type === "WebRTC"
            ? !!activeWebrtcUrl
            : getActiveTestImages().length > 0;
      }
    } else if (data?.Screen_type === "LLM") {
      isAdditionalValid =
        (selectedImages.length > 0 || selectedZips.length > 0) &&
        promptText.trim() !== "";
    } else if (data?.Screen_type === "VLM") {
      isAdditionalValid =
        promptText.trim() !== "" ||
        selectedImages.length > 0 ||
        selectedZips.length > 0;
    }
    const isFormComplete = isFamilyChipsetValid && isAdditionalValid;

    const handleStartEvaluation = () => {
      if (loadButton) return;

      setGuideActive(true);

      const missing = getNextMissingStep();
      if (missing) {
        showMissingStep(missing);
        return;
      }

      if (hasPendingMediaProcessing()) {
        showInfoPopover(
          "data-upload-section",
          "URL download or file loading is in progress. Please wait until it finishes."
        );
        return;
      }

      if (isCheckingNsfw) {
        showInfoPopover(
          "data-upload-section",
          "Please wait while uploaded images are checked for sensitive content.",
          2500,
          "danger"
        );
        return;
      }

      const totalUploadBytes = calculateTotalUploadSize();
      const totalUploadMB = bytesToMBNumber(totalUploadBytes);

      if (memRemainingMB >= 0 && totalUploadMB > memRemainingMB) {
        showInfoPopover(
          "data-upload-section",
          `Not enough memory to start evaluation. Upload size: ${totalUploadMB.toFixed(
            2
          )} MB, remaining: ${memRemainingMB.toFixed(2)} MB.`,
          3500,
          "danger"
        );
        return;
      }

      setGuideActive(false);
      handleSubmitNewEvaluation();
    };

    const available = useMemo(
      () => (data?.guidelines?.data_input_format || []).filter(
        (tool) => tool !== "network"
      ),
      [data?.guidelines?.data_input_format]
    );
    const displayedSources = useMemo(() => {
      if (!embedded) return available;

      // Project model versions do not always return the complete evaluation
      // guideline list. Keep the supported CV entry points available in the
      // embedded evaluation page even when that metadata is incomplete.
      return ["image", "s3", "stream", "dataset"];
    }, [available, embedded]);

    useEffect(() => {
      if (initialDataSource) {
        setSelectedTool(initialDataSource);
      }
    }, [initialDataSource]);

    useEffect(() => {
      const selectableSources = embedded ? displayedSources : available;

      if (!LIVE_EVALUATION_ENABLED && selectedTool === "stream") {
        setSelectedTool(
          embedded
            ? ""
            : selectableSources.find((source) => source !== "stream") || ""
        );
        return;
      }

      const selectedSourceIsDisabled =
        selectedTool === "dataset" ||
        selectedTool === "network" ||
        (datatype === "Dataset" && selectedTool === "stream");

      if (
        embedded &&
        selectedTool &&
        (!selectableSources.includes(selectedTool) || selectedSourceIsDisabled)
      ) {
        setSelectedTool("");
      } else if (!embedded && selectableSources.length && !selectableSources.includes(selectedTool)) {
        setSelectedTool(selectableSources[0]);
      }
    }, [available, datatype, displayedSources, embedded, selectedTool]);

    useEffect(() => {
      if (!embedded || !onActiveSourceChange) return;

      const sourceLabels = {
        image: "Local PC",
        s3: "Cloud",
        stream: "Live",
        dataset: "Platform",
      };

      onActiveSourceChange(sourceLabels[selectedTool] || "Select Data Source");
    }, [embedded, onActiveSourceChange, selectedTool]);

    const handleDatasetModeChange = (event) => {
      const datasetEnabled = event.target.checked;

      clearTestMedia();
      clearReferenceMedia();
      setDatatype(datasetEnabled ? "Dataset" : "Images");
      setFormat(datasetEnabled ? "" : annotationFormatConfig.defaultValue);
      setrawdata(true);

      // Dataset mode changes the accepted input contract. Start its custom
      // dataset flow with Local PC so the upload area is immediately available.
      if (datasetEnabled) {
        setSelectedTool("image");
      }
    };

    const renderSourceOptionTiles = () => (
      <div className="row g-2 cv-source-options">
        {displayedSources.map((tool, i) => {
          const { icon, title, description } = TOOL_CONFIG[tool] || {};
          if (!icon) return null;
          const isDatasetUpload = tool === "upload-dataset";
          const isSourceActive = embedded
            ? selectedTool === tool
            : isDatasetUpload
              ? selectedTool === "image" && datatype === "Dataset"
              : selectedTool === tool && datatype !== "Dataset";
          const isSourceDisabled =
            tool === "dataset" || tool === "network" ||
            (!LIVE_EVALUATION_ENABLED && tool === "stream") ||
            (embedded && datatype === "Dataset" && tool === "stream") ||
            (isDatasetUpload && isDatasetUploadDisabled);

          return (
            <div className="col-6 col-sm-4 mt-0" key={i}>
              <div
                id={`datasource-${tool}`}
                className={`em-source-tile d-flex justify-content-between gap-2 ${isSourceActive ? "active cpf-hw-item--active" : ""} ${isSourceDisabled ? "disabled" : ""}`}
                aria-disabled={isSourceDisabled}
                title={!LIVE_EVALUATION_ENABLED && tool === "stream"
                  ? "Live evaluation is temporarily unavailable"
                  : undefined}
                style={{
                  opacity: isSourceDisabled ? 0.5 : 1,
                  cursor: isSourceDisabled ? "not-allowed" : "pointer",
                  textAlign: 'justify'
                }}
                onClick={() => {
                  if (isSourceDisabled) return;

                  clearTestMedia();
                  clearReferenceMedia();
                  setreferenceS3Url("");
                  setreferencepresigneds3url("");
                  setS3Url("");
                  setpresigneds3url("");
                  if (embedded) {
                    setSelectedTool(tool);
                    return;
                  }
                  if (isDatasetUpload) {
                    setSelectedTool("image");
                    setDatatype("Dataset");
                    setFormat("");
                    return;
                  }
                  if (tool === "image" || tool === "s3" || tool === "stream") {
                    setDatatype("Images");
                  }
                  setSelectedTool(tool);
                }}
              >
                <div className="">
                  {icon}

                  <h6 className="em-source-label fw-semibold mt-1 mb-0">
                    {embedded
                      ? ({ image: "Local PC", s3: "Cloud", "upload-dataset": "Dataset", dataset: "Platform", stream: "Live" }[tool] || title)
                      : title}
                  </h6>
                  {embedded && description && (
                    <small className="cv-embedded-source-description">{description}</small>
                  )}
                </div>
                <div className="cpf-hw-dot"></div>
              </div>
            </div>
          );
        })}
      </div>
    );

    const handleStartStop = async (action) => {

      const url = startstopurl + `/${action}?visual_inspection=false`;
      // console.log(url);
      const response = await getDataFromApi(url, 'get');

      // console.log(response);
      if (evalStatus === "complete") {
        setStartStopstatus({ start: false, stop: true });

      } else if (evalStatus === "evaluating") {
        setStartStopstatus({ start: true, stop: false });
      }

      handleCallChildFunction();


    }


    const handleCallChildFunction = () => {
      if (childRef.current) {
        childRef.current.childFunction(); // Call the child's function
      }
    };

    const handleChange = (event) => {
      setFormat(event.target.value);
      clearTestMedia();
    };

    const handledatatypeChange = (event) => {
      setDatatype(event.target.value);
      clearTestMedia();
    };
    const handlestreamtypeChange = (event) => {
      setStreamtype(event.target.value);
      setActiveWebrtcUrl("");
      setWebrtcUrlError("");
    };

    const handleWebrtcUrlChange = (event) => {
      setWebrtcUrl(event.target.value);
      setActiveWebrtcUrl("");
      setWebrtcUrlError("");
    };

    const handleWebrtcStreamToggle = () => {
      if (activeWebrtcUrl) {
        setActiveWebrtcUrl("");
        return;
      }

      const value = webrtcUrl.trim();
      if (!value) {
        setWebrtcUrlError("Please provide a WebRTC URL");
        return;
      }

      try {
        const parsedUrl = new URL(value);
        if (!["http:", "https:"].includes(parsedUrl.protocol)) {
          throw new Error("Unsupported protocol");
        }

        const normalizedUrl = value.endsWith("/") ? value : `${value}/`;
        setWebrtcUrl(normalizedUrl);
        setActiveWebrtcUrl(normalizedUrl);
        setWebrtcUrlError("");
      } catch {
        setWebrtcUrlError("Enter a valid HTTP or HTTPS WebRTC URL");
      }
    };

    useEffect(() => {
      if (selectedTool !== "stream" || stream_type !== "PC_CAMERA") {
        setStreamSnapshots([]);
      }

      if (selectedTool !== "stream" || stream_type !== "WebRTC") {
        setActiveWebrtcUrl("");
      }
    }, [selectedTool, stream_type]);

    const handleRemoveStreamSnapshot = useCallback((snapshotId) => {
      pcCameraStreamRef.current?.removeSnapshot?.(snapshotId);
      setStreamSnapshots((prev) => prev.filter((snapshot) => snapshot.id !== snapshotId));
    }, []);

    const renderStreamPanel = () => (
      rawdata ? (
        <div className={`cv-stream-panel${embedded ? " cv-stream-panel--embedded" : ""}`}>
          <fieldset
            className="cv-stream-type-fieldset d-a w-100"
            style={{ border: "none", margin: 0, padding: 0 }}
          >
            <div className="w-100 d-a cv-stream-type-options" style={{ display: "flex", fontSize: embedded ? "12px" : "80%" }}>
              {[
                { label: "PC Camera", value: "PC_CAMERA" },
                { label: "Device", value: "WebRTC" },
              ].map((item) => (
                <label
                  key={item.value}
                  className={`d-a text-center cv-stream-type-option${
                    stream_type === item.value ? " is-active" : ""
                  }`}
                >
                  <input
                    type="radio"
                    name="streamtype"
                    value={item.value}
                    checked={stream_type === item.value}
                    onChange={handlestreamtypeChange}
                  />
                  {item.label}
                </label>
              ))}
            </div>
          </fieldset>

          {stream_type === "WebRTC" ? (
            <>
              <div className="d-flex align-items-stretch gap-2 mb-2">
                <input
                  id="webrtc"
                  type="url"
                  className={`plain-button2 flex-fill border rounded p-2 ${webrtcUrlError ? "border-danger" : ""}`}
                  placeholder="WebRTC URL (for example, http://host:8889/stream/)"
                  value={webrtcUrl}
                  onChange={handleWebrtcUrlChange}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      handleWebrtcStreamToggle();
                    }
                  }}
                />
                <button
                  type="button"
                  className={`btn text-nowrap ${activeWebrtcUrl ? "btn-outline-danger" : "btn-primary"}`}
                  onClick={handleWebrtcStreamToggle}
                  disabled={!activeWebrtcUrl && !webrtcUrl.trim()}
                >
                  {activeWebrtcUrl ? "Stop Stream" : "Start Stream"}
                </button>
              </div>

              {webrtcUrlError && (
                <div className="text-danger mb-2" style={{ fontSize: "0.78rem" }}>
                  {webrtcUrlError}
                </div>
              )}

              {activeWebrtcUrl ? (
                <VideoStreaming
                  streamurl={activeWebrtcUrl}
                  predictionsAPI={""}
                  style={"half"}
                  title={"Live Streaming Evaluation"}
                  eval_id={evalStatusRef.current?.evalId}
                  evalStatus={evalStatusRef.current?.status}
                  detailed_view={true}
                />
              ) : null}
            </>
          ) : (
            <PCCameraStream
              ref={pcCameraStreamRef}
              active={stream_type === "PC_CAMERA"}
              backendUrl={`${window["ENV"].API_URL}/models/live_camera_predict`}
              uploadSnapshotUrl={`${window["ENV"].API_URL}/evaluations/upload_snapshot`}
              uploadRecordingUrl={`${window["ENV"].API_URL}/evaluations/upload_recording`}
              modelId={data?.info?._id}
              evalId={evalStatusRef.current?.evalId}
              token={getCokkie("jwtToken")}
              onSnapshotsChange={setStreamSnapshots}
              hideSnapshotGallery={embedded}
              compactControls={embedded}
            />
          )}
        </div>
      ) : null
    );

    const renderEvaluationStartingState = () => (
      <>
        <FontAwesomeIcon
          icon={faSpinner}
          spin
          className="em-drop-icon mb-2 text-primary"
          aria-hidden="true"
        />
        <p className="em-drop-title mb-0">Starting evaluation...</p>
        <p className="em-drop-subtext mb-0">
          Preparing your input and loading the results.
        </p>
      </>
    );

    const flagAdultContentImage = (imageName, imageType) => {
      if (imageType === "test") {
        setFlaggedTestImages((prev) =>
          prev.includes(imageName) ? prev : [...prev, imageName]
        );
      }

      if (imageType === "reference") {
        setFlaggedReferenceImages((prev) =>
          prev.includes(imageName) ? prev : [...prev, imageName]
        );
      }
    };

    const checkAdultContent = async (imageUrl, imageName, imageType) => {
      if (nsfwCheckIdleTimerRef.current) {
        clearTimeout(nsfwCheckIdleTimerRef.current);
        nsfwCheckIdleTimerRef.current = null;
      }

      nsfwChecksInFlightRef.current += 1;
      if (nsfwChecksInFlightRef.current === 1) {
        setIsCheckingNsfw(true);
      }

      try {
        const model =
          nsfwModelRef.current ||
          (nsfwModelPromiseRef.current
            ? await nsfwModelPromiseRef.current
            : null);

        if (!model) return;

        const img = new Image();
        img.src = imageUrl;
        await new Promise((res, rej) => {
          img.onload = res;
          img.onerror = rej;
        });

        const predictions = await model.classify(img);
        const predictionMap = predictions.reduce((acc, prediction) => {
          acc[prediction.className] = prediction.probability;
          return acc;
        }, {});

        const explicitScore = Math.max(
          predictionMap.Porn || 0,
          predictionMap.Hentai || 0
        );
        const sexyScore = predictionMap.Sexy || 0;
        const adultScore = explicitScore + sexyScore;
        const isAdult =
          explicitScore >= NSFW_EXPLICIT_THRESHOLD ||
          sexyScore >= NSFW_SEXY_THRESHOLD ||
          (sexyScore >= NSFW_SEXY_WITH_EXPLICIT_THRESHOLD &&
            adultScore >= NSFW_ADULT_SUM_THRESHOLD);

        if (!isAdult) return;

        flagAdultContentImage(imageName, imageType);
      } catch (err) {
        console.error("NSFW check failed", err);
      } finally {
        nsfwChecksInFlightRef.current = Math.max(
          nsfwChecksInFlightRef.current - 1,
          0
        );

        if (nsfwChecksInFlightRef.current === 0) {
          nsfwCheckIdleTimerRef.current = setTimeout(() => {
            setIsCheckingNsfw(false);
            nsfwCheckIdleTimerRef.current = null;
          }, 300);
        }
      }
    };


    const calculateTotalUploadSize = () => {
      let totalBytes = 0;
      const activeTestImages = getActiveTestImages();

      // Test / main images & videos
      activeTestImages.forEach((item) => {
        if (item?.file?.size) {
          totalBytes += item.file.size;
        }
      });

      // Reference images (FaceRecognition)
      selectedrefernceImage.forEach((item) => {
        if (item?.file?.size) {
          totalBytes += item.file.size;
        }
      });

      // JSON annotation file (if any)
      if (jsonFile?.size) {
        totalBytes += jsonFile.size;
      }

      return totalBytes;
    };

    const bytesToMBNumber = (bytes) => bytes / (1024 * 1024);

    const isEmbeddedFacenet =
      embedded &&
      String(data?.info?.name || "").toLowerCase().includes("facenet");
    const isEmbeddedFacenetLocal =
      isEmbeddedFacenet && selectedTool === "image";

    return (
      <>
        <form
          className={`evaluate-model-ui p-0 ${embedded ? "cv-evaluation-field--embedded" : "h-100"}${
            isEmbeddedFacenet ? " cv-evaluation-field--facenet" : ""
          }${
            isEmbeddedFacenetLocal
              ? " cv-evaluation-field--facenet-local"
              : ""
          }`}
          style={{
            display: "flex",
            flexDirection: "column",
            overflow: embedded ? "visible" : "hidden"
          }}
        >

          {/* HEADER */}
          {!embedded && <div className="d-flex align-items-start justify-content-between mb-4 p-3" style={{ borderBottom: '1px solid var(--glass-color)', flexShrink: 0 }}>
            <div className="d-flex gap-2 align-tems-start w-100">
              <HistoryIcon className="wf-icon-blue bg-primary text-white" />
              <div className="em-header flex-fill">
                <div className="d-flex justify-content-between w-100">
                  <h2 className="em-title mb-0">Evaluate Model</h2>
                  <button
                    className="plain-button2 p-0"
                    onClick={(e) => { e.preventDefault(); setOpenEvalDrawer(false); setLoadButton(false) }}
                  >
                    <CloseIcon className="wf-icon text-secondary" />
                  </button>
                </div>
                <p className="em-subtitle mb-0">
                  Configure runtime for benchmarking <strong style={{ textTransform: 'capitalize' }}>{data?.info?.name}</strong>
                </p>
              </div>
            </div>
          </div>}


          {embedded && !hideTestDataSelection && (
            <section className="cv-embedded-source-tabs" aria-label="Data source">
              <div className="cv-embedded-source-selector">
                <div className="cv-embedded-source-selector__sources">
                  {renderSourceOptionTiles()}
                </div>

                <label
                  className={`cv-embedded-dataset-toggle${
                    datatype === "Dataset" ? " is-active" : ""
                  }${isDatasetUploadDisabled ? " is-disabled" : ""}`}
                  title={
                    isDatasetUploadDisabled
                      ? "Dataset evaluation is not supported for this model"
                      : "Enable dataset mode"
                  }
                >
                  <input
                  style={{display:"none"}}
                    type="checkbox"
                    checked={datatype === "Dataset"}
                    disabled={isDatasetUploadDisabled || loadButton}
                    onChange={handleDatasetModeChange}
                  />
                  <span>Dataset</span>
                </label>
              </div>

            </section>
          )}

          <div
            ref={formScrollRef}
            className={`d-flex flex-column h-md-auto cv-evaluation-field-scroll ${
              embedded ? "cv-embedded-composer-card px-0" : "px-4"
            }${embedded && selectedTool === "stream" ? " cv-evaluation-field-scroll--stream" : ""}`}
            style={{
              minHeight: 0,
              overflowY: embedded ? "visible" : "auto",
              overflowX: "hidden"
            }}
          >

            {/* TARGET HARDWARE */}
            <div className="d-a mb-2 cv-hardware-heading">
              <ChipsetIcon className="wf-icon" />
              <h6 className="mb-0"> Select Target Hardware</h6>
            </div>
            <section className="em-section card p-3 mb-4 cv-runtime-controls">
              <EvaluationTargetHardwareMenu
                devices={data?.devices}
                selectedTargetHardware={selectedTargetHardware}
                onTargetHardwareChange={setSelectedTargetHardware}
                selectedFamily={selectedFamily}
                onFamilyChange={setSelectedFamily}
                selectedChipset={selectedChipset}
                onChipsetChange={setSelectedChipset}
                selectedDevice={selectedDevice}
                onDeviceChange={setSelectedDevice}
                getDeviceStatus={getDeviceStatus}
                disabled={loadButton}
                familyRef={familyRef}
                chipsetRef={chipsetRef}
                useDropdownSelections
                centerHardwareFamily
                triggerId="cv-target-hardware-trigger"
              />
            </section>




            {/* DATA SOURCE SECTION */}
            {!hideTestDataSelection && !embedded && <section className="mb-4">
              <div className="d-a gap-1 mb-2">
                <DatasetIcon className="wf-icon-small" />
                <h6 className="mb-0 ps-1"> Select Test Data</h6>
              </div>

              {/* TAB SWITCH */}
              <div className="em-tabs d-flex p-1">
                <button
                  type="button"
                  className={`em-tab flex-fill fw-bold ${rawdata ? "active" : "text-muted"
                    }`}
                  onClick={() => setrawdata(true)}
                >
                  Custom Data
                </button>

                <button
                  id="test-dataset-btn"
                  disabled={window.ENV.CUSTOMER === "ambarella"}
                  type="button"
                  className={`em-tab flex-fill fw-bold  ${!rawdata ? "active" : "text-muted border-0"
                    }`}
                  // onClick={() => setrawdata(false)}
                onClick={() =>
                  showInfoPopover(
                    "test-dataset-btn",
                    "Benchmark datasets are coming soon. You’ll be able to benchmark models using curated datasets."
                  )
                  // setSelectedDatasetId(true)

                }
                >
                  Benchmark Datasets
                </button>
              </div>


            </section>}

            {/* SOURCE TYPE GRID */}
            <section className="cv-source-config">
              {!hideTestDataSelection && !embedded && <>
                <div className="d-block mb-2 fw-bold cv-source-options-label" style={{ color: '#6c757d' }}>Data Source</div>

                {renderSourceOptionTiles()}
              </>}

              {(selectedTool === "image" || selectedTool === "s3") &&
                <>{rawdata &&
                  <>
                    <div className="d-flex align-items-end justify-content-between gap-3 mt-3 cv-original-data-type-controls">
                      <div>
                        <div className="d-block fw-bold" style={{ color: '#6c757d' }}>Data Type</div>

                        <fieldset className="d-a" style={{ border: "none", margin: 0, padding: 0 }}>

                          <div className="d-a" style={{ display: "flex", fontSize: '80%' }}>

                            {['Images', 'Dataset'].map((item) => {

                              const isDataTypeDisabled =
                                (item === "Dataset" &&
                                  (data?.info?.task_type === "Depth Estimation" ||
                                    data?.info?.name === "RTMDet" ||
                                    isSuperpointKeyPointDetection ||
                                    isFsrcnnImageEnhancement));
                              return (
                                <label
                                  key={item}
                                  className={`d-a text-center`}
                                  style={{
                                    padding: "10px",
                                    cursor: isDataTypeDisabled ? "not-allowed" : "pointer",
                                    opacity: isDataTypeDisabled ? 0.5 : 1,
                                  }}
                                >
                                  <input
                                    type="radio"
                                    name="datatype"
                                    value={item}
                                    checked={datatype === item}
                                    onChange={handledatatypeChange}
                                    // style={{ display: "none" }}
                                    disabled={isDataTypeDisabled}
                                  />
                                  &nbsp;{item}
                                </label>
                              )
                            })}
                          </div>
                        </fieldset>
                      </div>

                      {!hideTestDataSelection && !embedded && selectedTool === "image" &&
                        datatype === "Images" && (
                          <div
                            className="llm-image-suggestions align-items-end"
                            style={{ minWidth: 0, gap: "4px" }}
                            aria-label="Sample images"
                          >
                            <span className="llm-prompt-suggestions-label text-muted">
                              Try sample images
                            </span>
                            <div className="d-flex gap-2 justify-content-end overflow-auto pb-1">
                              {suggestedImageOptions.map((suggestedImage) => {
                                const activeSuggestedImages =
                                  data?.info?.task_type === "FaceRecognition" && references3file
                                    ? selectedrefernceImage
                                    : selectedImages;
                                const isLoading =
                                  selectingSuggestedImage === suggestedImage.name;
                                const isSelected =
                                  isLoading || activeSuggestedImages.some(
                                    (image) => image?.name === suggestedImage.name
                                  );

                                return (
                                  <button
                                    key={suggestedImage.name}
                                    type="button"
                                    className={`llm-image-suggestion ${
                                      isSelected ? "selected" : ""
                                    }`}
                                    style={{ flexBasis: "72px", width: "72px" }}
                                    onClick={(event) =>
                                      handleSuggestedImageSelect(
                                        suggestedImage,
                                        "",
                                        event.currentTarget.querySelector("img")
                                      )
                                    }
                                    disabled={
                                      loadButton ||
                                      Boolean(selectingSuggestedImage) ||
                                      isProcessingFiles ||
                                      isProcessingZip
                                    }
                                    aria-pressed={isSelected}
                                    aria-label={`${isSelected ? "Remove" : "Select"} ${suggestedImage.label}`}
                                    title={`${isSelected ? "Remove" : "Select"} ${suggestedImage.label}${isLoading ? " (loading)" : ""}`}
                                  >
                                    <img
                                      src={suggestedImage.src}
                                      alt={suggestedImage.label}
                                      style={{ height: "40px" }}
                                      loading="lazy"
                                      decoding="async"
                                    />
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}
                    </div>
                  </>
                }
                  {!embedded && datatype === "Dataset" && (
                    <div className="cv-annotation-format-field">
                      <label htmlFor="cv-drawer-annotation-format">Annotation Format</label>
                      <Select
                        inputId="cv-drawer-annotation-format"
                        className="eval-target-hardware-panel__react-select cv-annotation-format-react-select"
                        classNamePrefix="eval-target-device-select"
                        options={annotationFormatOptions}
                        value={
                          annotationFormatOptions.find(
                            (option) => option.value === format
                          ) || null
                        }
                        placeholder="Annotation Format"
                        isSearchable={false}
                        menuPlacement="auto"
                        menuShouldScrollIntoView={false}
                        components={ANNOTATION_SELECT_COMPONENTS}
                        onChange={(option) => {
                          setFormat(option?.value || "");
                          clearTestMedia();
                        }}
                      />
                    </div>
                  )}
                </>}

              {!embedded && selectedTool === "stream" && renderStreamPanel()}
            </section>





            {data?.info.task_type === "FaceRecognition" && (
              <div className={`d-a${isEmbeddedFacenet ? " cv-embedded-facenet-options" : ""}`}>
                <fieldset className={`d-a${isEmbeddedFacenet ? " w-100" : " ms-1"}`} style={{ border: "none", margin: 0, padding: 0 }}>
                  <div className="w-100 d-a cv-facenet-file-type-options" style={{ display: "flex" }}>
                    <label
                      htmlFor="facenet-reference-data"
                      className={`d-a text-center${
                        isEmbeddedFacenet
                          ? " form-label cursor-pointer"
                          : references3file ? "" : " text-secondary"
                      }`}
                      style={{
                        fontSize: isEmbeddedFacenet ? "80%" : undefined,
                        cursor: "pointer",
                      }}
                    >
                      <input
                        id="facenet-reference-data"
                        type="radio"
                        name="fileType"
                        className={isEmbeddedFacenet ? "border rounded p-2" : undefined}
                        value="reference"
                        checked={references3file}
                        onChange={() => setreferences3file(true)}
                      // style={{ display: "none" }}
                      />&nbsp;
                      Reference Data
                    </label>

                    <label
                      htmlFor="facenet-test-data"
                      className={`d-a text-center${
                        isEmbeddedFacenet
                          ? " form-label cursor-pointer"
                          : references3file ? " text-secondary" : ""
                      }`}
                      style={{
                        // borderTop: "1px solid #eee",
                        padding: isEmbeddedFacenet ? undefined : "10px",
                        fontSize: isEmbeddedFacenet ? "80%" : undefined,
                        cursor: "pointer",
                      }}
                    >
                      <input
                        id="facenet-test-data"
                        type="radio"
                        name="fileType"
                        className={isEmbeddedFacenet ? "border rounded p-2" : undefined}
                        value="test"
                        checked={!references3file}
                        onChange={() => setreferences3file(false)}
                      // style={{ display: "none" }}
                      />&nbsp;
                      Test Data
                    </label>
                  </div>
                </fieldset>
              </div>)}

            {/* UPLOAD AREA */}
            <section
              className={`flex-fill mb-3 cv-source-input${
                selectedTool === "stream" ? " cv-source-input--stream" : ""
              }`}
              ref={dataUploadRef}
              id="data-upload-section"
            >
              {selectedTool === "stream" && renderStreamPanel()}

              {nsfwWarning && (selectedImages.length > 0 || selectedrefernceImage.length > 0) && (
                <div className="cv-sensitive-content-warning mt-2">
                  <span>
                    ⚠️ Sensitive/adult content detected in
                    {flaggedTestImages.length > 0 && " test data"}
                    {flaggedTestImages.length > 0 &&
                      flaggedReferenceImages.length > 0 && " and"}
                    {flaggedReferenceImages.length > 0 && " reference data"}.
                  </span>
                  {(!isProcessingFiles || !isProcessingZip) &&
                    <button
                      type="button"
                      className="cv-sensitive-content-warning__remove plain-button2"
                      onClick={removeFlaggedImages}
                    >
                      Remove
                    </button>}
                </div>
              )}





              {selectedTool === "image" && (
                <div className={`h-100 ${data?.info.task_type === "FaceRecognition" ? "d-a gap-2" : " "}`}>
                  {references3file &&
                    <>
                      <div className="w-100 h-100">
                        {(isProcessingFiles || isProcessingZip) ? (
                          <div className="d-a gap-2"> Extracting <FontAwesomeIcon icon={faSpinner} spin /> </div>
                        ) :
                          <>
                            {selectedrefernceImagefiles.length > 0 &&
                              <div className="w-100 text-end cursor-pointer text-danger pe-4"
                                style={{ fontSize: '75%' }}
                                onClick={() => {
                                  clearReferenceMedia();
                                }
                                }>Clear All</div>}</>
                        }
                        <div
                          ref={referenceDropzoneRef}
                          className={`em-dropzone text-center ${loadButton || (selectedrefernceImagefiles.length === 0 && selectedrefernceZip.length === 0) ? "h-100 d-j-a" : ""} d-flex flex-column overflow-auto`}
                          style={{
                            textTransform: "none",
                            border: dragOver ? "2px dashed var()--leading-color" : selectedTool === "image" ? "2px dashed var(--glass-color)" : 'none',
                            cursor: "pointer",
                            maxHeight: '400px',
                            scrollbarWidth: "none"
                          }}
                          onScroll={(e) => {
                            const el = e.currentTarget;

                            const tolerance = 8; // px (tight = accurate)
                            const distanceFromBottom =
                              el.scrollHeight - el.scrollTop - el.clientHeight;

                            shouldAutoScrollRef.current = distanceFromBottom <= tolerance;
                          }}

                          onDragOver={handleDragOver}
                          onDragLeave={handleDragLeave}
                          onDrop={handlereferenceFileDrop}
                          onClick={handlerefernceFileSelect}
                        >

                          {loadButton && renderEvaluationStartingState()}

                          {!loadButton && selectedrefernceImagefiles.length === 0 && selectedrefernceZip.length === 0 && (
                            <>
                              <UploadIcon className="em-drop-icon mb-2 wf-icon" />
                              <p className="em-drop-title mb-0">Drag & Drop files</p>
                              <p className="em-drop-subtext">or browse your computer</p>
                              <p className="em-drop-subtext mb-0">
                                {/* Supported file formats: .zip, .jpg, .jpeg, .png */}
                                {datatype === "Dataset" ? "Supported file formats: .zip" : "Supported file formats: images and .zip"}

                              </p>
                            </>
                          )}

                          {!loadButton && (selectedrefernceImagefiles.length > 0 || selectedrefernceZip.length > 0) &&
                            <>
                              <div
                                ref={refThumbWrapRef}
                                onScroll={onRefThumbScroll}
                              // className="d-a flex-column"
                              // style={{ overflow: "auto", width: "100%", maxHeight: 220 }} // set a viewport height (important)
                              >

                              {selectedrefernceImagefiles.length > 0 && (


                                <VirtualImageGrid
                                  items={selectedrefernceImage}
                                  onRemove={handleRemovereferenceImage}
                                  flaggedImages={flaggedReferenceImages}
                                />


                              )}
                              {selectedrefernceZip.length > 0 && (
                                <div className="my-3">
                                  {selectedrefernceZip.map((zip, idx) => (
                                    <div
                                      key={idx}
                                      style={{
                                        position: "relative",
                                        display: "inline-block",
                                        marginRight: "10px",
                                        padding: "5px",
                                        border: "1px solid #ccc",
                                        borderRadius: "5px",
                                      }}
                                    >
                                      <span>{zip.name}</span>
                                      <FontAwesomeIcon
                                        icon={faTimesCircle}
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          handleRemovereferenceZip(idx);
                                        }}
                                        style={{
                                          position: "absolute",
                                          top: "-5px",
                                          right: "-5px",
                                          cursor: "pointer",
                                          background: "white",
                                          borderRadius: "50%",
                                        }}
                                      />
                                    </div>
                                  ))}
                                </div>
                              )}
                              </div>
                              <div className="em-dropzone-add-more">
                                <UploadIcon className="wf-icon" />
                                <span>Drag &amp; drop more files here, or click to browse</span>
                              </div>
                            </>}
                        </div>
                        <input
                          type="file"
                          ref={fileInputRef2}
                          style={{ display: "none" }}
                          multiple
                          accept={
                            datatype === "Dataset"
                                ? "application/zip,application/x-zip-compressed,.zip"
                                : "image/*,.jpg,.jpeg,.png,.webp,.bmp,.gif,application/zip,application/x-zip-compressed,.zip"
                          }
                          onChange={handlereferenceFileChange}
                        />
                      </div></>}

                  {!references3file &&
                    <>


                      <div className="w-100 h-100">
                        {(isProcessingFiles || isProcessingZip) ? (
                          <div className="d-a gap-2"> Extracting <FontAwesomeIcon icon={faSpinner} spin /> </div>
                        ) : <>
                          {selectedImages.length > 0 && (
                            <div
                              className="w-100 text-end cursor-pointer text-danger pe-4"
                              style={{ fontSize: '75%' }}
                              onClick={() => {
                                clearTestMedia();
                              }}
                            >
                              Clear All
                            </div>
                          )}</>}
                        <div
                          ref={testDropzoneRef}
                          className={`em-dropzone text-center ${loadButton || (selectedImages.length === 0 && selectedZips.length === 0) ? "h-100 d-j-a" : ""} d-flex flex-column overflow-auto`}
                          style={{ border: dragOver ? "2px dashed var(--leading-color)" : selectedTool === "image" ? "2px dashed var(--glass-color)" : 'none', maxHeight: '400px', scrollbarWidth: "none" }}
                          onDragOver={handleDragOver}
                          onDragLeave={handleDragLeave}
                          onDrop={handleFileDrop}
                          onClick={handleFileSelect}
                          onScroll={(e) => {
                            const el = e.currentTarget;

                            const tolerance = 8; // px (tight = accurate)
                            const distanceFromBottom =
                              el.scrollHeight - el.scrollTop - el.clientHeight;

                            shouldAutoScrollRef.current = distanceFromBottom <= tolerance;
                          }}

                        >
                          {loadButton && renderEvaluationStartingState()}

                          {!loadButton && selectedImages.length === 0 && selectedZips.length === 0 && (
                            <>
                              <UploadIcon className="em-drop-icon mb-2 wf-icon" />
                              <p className="em-drop-title mb-0">Drag & Drop files</p>
                              <p className="em-drop-subtext">or browse your computer</p>
                              <p className="em-drop-subtext mb-0">
                                {/* Supported file formats: .zip, .jpg, .jpeg, .png */}
                                {datatype === "Dataset" ? "Supported file formats: .zip" : "Supported file formats: images and .zip"}
                              </p>
                            </>
                          )}


                          {!loadButton && (selectedImages.length > 0 || selectedZips.length > 0) && (
                            <>
                              <div
                              // className={`${(selectedImages.length > 0 || selectedZips.length > 0) && "w-100"} d-a flex-column`}
                              >
                                {selectedImages.length > 0 && (
                                  <VirtualImageGrid
                                    items={selectedImages}
                                    onRemove={handleRemoveImage}
                                    flaggedImages={flaggedTestImages}
                                  />
                                )}

                                {selectedZips.length > 0 && (
                                  <div className="my-3">
                                    {selectedZips.map((zip, idx) => (
                                      <div
                                        key={idx}
                                        style={{
                                          position: "relative",
                                          display: "inline-block",
                                          marginRight: "10px",
                                          padding: "5px",
                                          border: "1px solid #ccc",
                                          borderRadius: "5px",
                                        }}
                                      >
                                        <span>{zip.name}</span>
                                        <FontAwesomeIcon
                                          icon={faTimesCircle}
                                          onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            handleRemoveZip(idx);
                                          }}
                                          style={{
                                            position: "absolute",
                                            top: "-5px",
                                            right: "-5px",
                                            cursor: "pointer",
                                            background: "white",
                                            borderRadius: "50%",
                                          }}
                                        />
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                              <div className="em-dropzone-add-more">
                                <UploadIcon className="wf-icon" />
                                <span>Drag &amp; drop more files here, or click to browse</span>
                              </div>
                            </>
                          )}
                        </div>

                        <input
                          type="file"
                          ref={fileInputRef}
                          style={{ display: "none" }}
                          multiple
                          accept={
                            datatype === "Dataset"
                                ? "application/zip,application/x-zip-compressed,.zip"
                                : "image/*,.jpg,.jpeg,.png,.webp,.bmp,.gif,application/zip,application/x-zip-compressed,.zip"
                          }
                          onChange={handleFileChange}
                        />
                      </div></>
                  }

                </div>
              )}

              {selectedTool === "s3" && (
                <div className="container-card cv-embedded-cloud-options mb-2 p-3 h-100">

                  {references3file && <div>
                    <div className="d-a">
                      <label htmlFor="s3-public" className="d-a form-label cursor-pointer" style={{fontSize: '80%' }}>
                        <input
                          id="s3-public"
                          type="radio"
                          name="option"
                          className="border rounded p-2 "
                          value="public"
                          checked={referenceprivateS3 === "public"}
                          onChange={() => {
                            setreferencePrivateS3("public");
                            setreferencepresigneds3url("");
                            clearReferenceMedia();
                          }
                          }
                        />&nbsp;
                        Public URL
                      </label> &nbsp;&nbsp;


                      <label htmlFor="s3-presigned" className="d-a form-label cursor-pointer" style={{fontSize: '80%' }}>
                        <input
                          id="s3-presigned"
                          type="radio"
                          name="option"
                          className=" border rounded p-2 "
                          value="presigned"
                          checked={referenceprivateS3 === "presigned"}
                          onChange={() => {
                            setreferencePrivateS3("presigned");
                            setreferenceS3Url("");
                            clearReferenceMedia();
                          }
                          }
                        />&nbsp;
                        Presigned URL
                      </label>&nbsp;&nbsp;

                      {/* <label htmlFor="s3-iamuser" className="d-a form-label cursor-pointer">
                        <input
                          id="s3-iamuser"
                          type="radio"
                          name="option"
                          className="border rounded p-2 "
                          value="iamuser"
                          checked={referenceprivateS3 === "iamuser"}
                          onChange={() => setreferencePrivateS3("iamuser")}
                        />&nbsp;
                        IAM user
                      </label> */}

                    </div>
                    {referenceprivateS3 === "iamuser" && (

                      <div className="ps-1 pt-2">
                        <div className="d-a gap-1 pb-2">
                          <input
                            type="text"
                            className="plain-button2 w-100 border rounded p-2"
                            placeholder="Access key"
                            value={references3Credentials.accessKey}
                            onChange={(e) =>
                              setreferenceS3Credentials((prev) => ({ ...prev, accessKey: e.target.value }))
                            }
                          />

                          <input
                            type="text"
                            className="plain-button2 w-100 border rounded p-2"
                            placeholder="Secret Key"
                            value={references3Credentials.secretKey}
                            onChange={(e) =>
                              setreferenceS3Credentials((prev) => ({ ...prev, secretKey: e.target.value }))
                            }
                          />
                        </div>

                        <div className="d-a gap-1 pb-2">
                          <input
                            type="text"
                            className="plain-button2 w-100 border rounded p-2"
                            placeholder="Role Id"
                            value={references3Credentials.roleId}
                            onChange={(e) =>
                              setreferenceS3Credentials((prev) => ({ ...prev, roleId: e.target.value }))
                            }
                          />

                          <input
                            type="text"
                            className="plain-button2 w-100 border rounded p-2"
                            placeholder="Role"
                            value={references3Credentials.role}
                            onChange={(e) =>
                              setreferenceS3Credentials((prev) => ({ ...prev, role: e.target.value }))
                            }
                          />

                          <input
                            type="text"
                            className="plain-button2 w-100 border rounded p-2"
                            placeholder="AWS region"
                            value={references3Credentials.region}
                            onChange={(e) =>
                              setreferenceS3Credentials((prev) => ({ ...prev, region: e.target.value }))
                            }
                          />
                        </div>
                        {/* <label htmlFor="s3-bucket" className="form-label">
                          Enter S3 Bucket
                        </label> */}
                        <input
                          id="s3-bucket"
                          type="text"
                          className="plain-button2 w-100 border rounded p-2 mb-2"
                          placeholder="S3 Bucket"
                          value={references3Credentials.s3bucket}
                          onChange={(e) =>
                            setreferenceS3Credentials((prev) => ({ ...prev, s3bucket: e.target.value }))
                          }
                        />

                        {/* <label htmlFor="s3-url" className="form-label">
                          Enter S3 URL
                        </label> */}
                        <input
                          id="s3-url"
                          type="text"
                          className="plain-button2 w-100 border rounded p-2"
                          placeholder="https://s3.amazonaws.com/your-bucket/your-file.zip"
                          value={references3Credentials.s3url}
                          onChange={(e) =>
                            setreferenceS3Credentials((prev) => ({ ...prev, s3url: e.target.value }))
                          }
                        />
                      </div>
                    )}
                    {referenceprivateS3 === "presigned" && (
                      <div className="pt-2 ps-1">
                        {/* <label htmlFor="s3-url" className="form-label">
                          Provide presigned url
                        </label> */}
                        <input
                          id="s3-url"
                          type="text"
                          className="plain-button2 w-100 border rounded p-2"
                          placeholder="https://platform-upload.s3.ap-south-1.amazonaws.com/data.zip?response-content-disposition=inline&X-Amz-Content-Sha256=UNSIGNED-PAYLOAD&X-Amz-Security-Token=IQoJb3JpZ2luX2VjEEcaCmFwLXNvdXRoLTEiRzBFAiArG20k%2FhIGAy1hpnBcIEC5cH52Aw"
                          value={referencepresigneds3url}
                          onChange={(e) => {
                            setreferencepresigneds3url(e.target.value);
                            handlereferenceS3ZipProcessing(e.target.value);
                          }}
                        />
                        <div className="text-secondary " style={{ fontSize: "12px" }}>Note:To generate a pre-signed URL, go to the S3 console, select the file, click Actions, and choose "Generate presigned URL" to create a secure, time-limited download link.</div>
                      </div>
                    )}




                    {referenceprivateS3 === "public" && (
                      <div className="pt-2">
                        {/* <label htmlFor="s3-bucket" className="form-label">
                          Enter S3 Bucket
                        </label> */}
                        {/* <input
                          id="s3-bucket"
                          type="text"
                          className="plain-button2 w-100 border rounded p-2"
                          placeholder="S3 Bucket"
                          value={references3BucketName}
                          onChange={(e) => setreferenceS3BucketName(e.target.value)}
                        /> */}

                        <div className="d-a pt-2 gap-1">
                          {/* <label htmlFor="s3-url" className="form-label">
                            Enter S3 URL
                          </label> */}
                          <input
                            id="s3-url"
                            type="text"
                            className="plain-button2 w-100 border rounded p-2"
                            placeholder="https://s3.amazonaws.com/your-bucket/your-file.zip"
                            value={references3Url}
                            onChange={(e) => {
                              setreferenceS3Url(e.target.value);
                              handlereferenceS3ZipProcessing(e.target.value)
                            }}
                          />
                        </div>
                      </div>
                    )}

                  </div>}
                  {!references3file && <div>
                    <div className="d-a">
                      <label htmlFor="s3-public" className="d-a form-label cursor-pointer" style={{fontSize: '80%' }}>
                        <input
                          id="s3-public"
                          type="radio"
                          name="option"
                          className=" border rounded p-2 "
                          value="public"
                          checked={privateS3 === "public"}
                          onChange={() => {
                            setPrivateS3("public");
                            setpresigneds3url("");
                            clearTestMedia();
                          }
                          }
                        />&nbsp;
                        Public URL
                      </label> &nbsp;&nbsp;




                      <label htmlFor="s3-presigned" className="d-a form-label cursor-pointer" style={{fontSize: '80%' }}>
                        <input
                          id="s3-presigned"
                          type="radio"
                          name="option"
                          className="border rounded p-2 "
                          value="presigned"
                          checked={privateS3 === "presigned"}
                          onChange={() => {
                            setPrivateS3("presigned");
                            setS3Url("");
                            clearTestMedia();
                          }}
                        />&nbsp;
                        Presigned URL
                      </label>&nbsp;&nbsp;

                      {/* <label htmlFor="s3-iamuser" className="d-a form-label cursor-pointer">
                        <input
                          id="s3-iamuser"
                          type="radio"
                          name="option"
                          className="border rounded p-2 "
                          value="iamuser"
                          checked={privateS3 === "iamuser"}
                          onChange={() => setPrivateS3("iamuser")}
                        />&nbsp;
                        IAM user
                      </label> */}

                    </div>
                    {privateS3 === "iamuser" && (

                      <div className="ps-1 pt-2">
                        <div className="d-a gap-1 pb-2">
                          <input
                            type="text"
                            className="plain-button2 w-100 border rounded p-2"
                            placeholder="Access key"
                            value={s3Credentials.accessKey}
                            onChange={(e) =>
                              setS3Credentials((prev) => ({ ...prev, accessKey: e.target.value }))
                            }
                          />

                          <input
                            type="text"
                            className="plain-button2 w-100 border rounded p-2"
                            placeholder="Secret Key"
                            value={s3Credentials.secretKey}
                            onChange={(e) =>
                              setS3Credentials((prev) => ({ ...prev, secretKey: e.target.value }))
                            }
                          />
                        </div>

                        <div className="d-a gap-1 pb-2">
                          <input
                            type="text"
                            className="plain-button2 w-100 border rounded p-2"
                            placeholder="Role Id"
                            value={s3Credentials.roleId}
                            onChange={(e) =>
                              setS3Credentials((prev) => ({ ...prev, roleId: e.target.value }))
                            }
                          />

                          <input
                            type="text"
                            className="plain-button2 w-100 border rounded p-2"
                            placeholder="Role"
                            value={s3Credentials.role}
                            onChange={(e) =>
                              setS3Credentials((prev) => ({ ...prev, role: e.target.value }))
                            }
                          />

                          <input
                            type="text"
                            className="plain-button2 w-100 border rounded p-2"
                            placeholder="AWS region"
                            value={s3Credentials.region}
                            onChange={(e) =>
                              setS3Credentials((prev) => ({ ...prev, region: e.target.value }))
                            }
                          />
                        </div>
                        {/* <label htmlFor="s3-bucket" className="form-label">
                          Enter S3 Bucket
                        </label> */}
                        <input
                          id="s3-bucket"
                          type="text"
                          className="plain-button2 w-100 border rounded p-2 mb-2"
                          placeholder="S3 Bucket"
                          value={s3Credentials.s3bucket}
                          onChange={(e) =>
                            setS3Credentials((prev) => ({ ...prev, s3bucket: e.target.value }))
                          }
                        />

                        {/* <label htmlFor="s3-url" className="form-label">
                          Enter S3 URL
                        </label> */}
                        <div className="d-a gap-1">
                          <input
                            id="s3-url"
                            type="text"
                            className="plain-button2 w-100 border rounded p-2"
                            placeholder="https://s3.amazonaws.com/your-bucket/your-file.zip"
                            value={s3Credentials.s3url}
                            onChange={(e) => {
                              setS3Credentials((prev) => ({ ...prev, s3url: e.target.value }));
                              handleS3ZipProcessing(e.target.value);
                            }
                            }
                          />
                          {/* <FontAwesomeIcon className="cursor-pointer" icon={faDownload} color="#1a73e8" onClick={() => handleS3ZipProcessing(s3Credentials.s3url)} /> */}
                        </div>
                      </div>
                    )}
                    {privateS3 === "presigned" && (
                      <div className="pt-2 ps-1">
                        {/* <label htmlFor="s3-url" className="form-label">
                          Provide presigned url
                        </label> */}
                        <div className="d-a gap-1">
                          <input
                            id="s3-url"
                            type="text"
                            className="plain-button2 w-100 border rounded p-2"
                            placeholder="https://platform-upload.s3.ap-south-1.amazonaws.com/data.zip?response-content-disposition=inline&X-Amz-Content-Sha256=UNSIGNED-PAYLOAD&X-Amz-Security-Token=IQoJb3JpZ2luX2VjEEcaCmFwLXNvdXRoLTEiRzBFAiArG20k%2FhIGAy1hpnBcIEC5cH52Aw"
                            value={presigneds3url}
                            onChange={(e) => {
                              setpresigneds3url(e.target.value);
                              handleS3ZipProcessing(e.target.value);
                            }}
                          />
                          {/* <FontAwesomeIcon className="cursor-pointer" icon={faDownload} color="#1a73e8" onClick={() => handleS3ZipProcessing(presigneds3url)} /> */}
                        </div>
                        <div className="text-secondary " style={{ fontSize: "12px" }}>Note:To generate a pre-signed URL, go to the S3 console, select the file, click Actions, and choose "Generate presigned URL" to create a secure, time-limited download link.</div>
                      </div>
                    )}




                    {privateS3 === "public" && (
                      <div className="pt-2">
                        {/* <label htmlFor="s3-bucket" className="form-label">
                          Enter S3 Bucket
                        </label> */}
                        {/* <input
                          id="s3-bucket"
                          type="text"
                          className="plain-button2 w-100 border rounded p-2"
                          placeholder="S3 Bucket"
                          value={s3BucketName}
                          onChange={(e) => setS3BucketName(e.target.value)}
                        /> */}

                        <div className="d-a pt-2 gap-1">
                          {/* <label htmlFor="s3-url" className="form-label">
                            Enter S3 URL
                          </label> */}
                          <input
                            id="s3-url"
                            type="text"
                            className="plain-button2 w-100 border rounded p-2"
                            placeholder="https://s3.amazonaws.com/your-bucket/your-file.zip"
                            value={s3Url}
                            onChange={(e) => {
                              setS3Url(e.target.value);
                              handleS3ZipProcessing(e.target.value);
                            }}
                          />
                          {/* <FontAwesomeIcon className="cursor-pointer" icon={faDownload} color="#1a73e8" onClick={() => handleS3ZipProcessing(s3Url)} /> */}

                        </div>
                      </div>
                    )}

                  </div>}
                </div>
              )}

              {selectedTool === "network" && (
                <div className="container-card mb-2 p-3 h-100">
                  {/* {data?.info.task_type === "FaceRecognition" &&
                    <div className="d-a w-100 pb-2" style={{ borderTop: "1px solid #eee" }}>
                      <button
                        type="button"
                        className={`${referencenetworkfile ? "" : "text-secondary"} w-50`}
                        style={{ border: "none", background: "none", borderTop: "1px solid #eee", borderRight: "1px solid #eee" }}
                        onClick={() => {
                          if (!referencenetworkfile) setreferencenetworkfile(true);
                        }}
                      >
                        Reference File
                      </button>
                      <button
                        type="button"
                        className={`${referencenetworkfile ? "text-secondary" : ""} w-50`}
                        style={{ border: "none", background: "none", borderTop: "1px solid #eee" }}
                        onClick={() => {
                          if (referencenetworkfile) setreferencenetworkfile(false);
                        }}
                      >
                        Test File
                      </button>
                    </div>} */}
                  {references3file && <div>
                    <div className="d-a">
                      <label htmlFor="network-Host" className="form-label">
                        Host
                      </label>
                      &nbsp;
                      <input
                        id="network-Host"
                        type="text"
                        className="plain-button2 w-100 border rounded p-2"
                        placeholder="e.g. 192.168.1.100"
                        value={referencenetworkfields.networkhost}
                        onChange={(e) => setreferencenetworkfields((prev) => ({ ...prev, networkhost: e.target.value }))}
                      />
                      &nbsp;&nbsp;
                      <label htmlFor="network-port" className="form-label">
                        Port
                      </label>&nbsp;&nbsp;
                      <input
                        id="network-port"
                        type="number"
                        className="plain-button2 w-100 border rounded p-2"
                        placeholder="e.g. 8080"
                        value={referencenetworkfields.networkport}
                        onChange={(e) => setreferencenetworkfields((prev) => ({ ...prev, networkport: e.target.value }))}
                      />
                    </div>
                    <div className="d-a pt-2">

                      <input
                        id="network-user"
                        type="text"
                        className="plain-button2 w-100 border rounded p-2"
                        placeholder="User"
                        value={referencenetworkfields.networkuser}
                        onChange={(e) => setreferencenetworkfields((prev) => ({ ...prev, networkuser: e.target.value }))}
                      />&nbsp;&nbsp;

                      <input
                        id="network-password"
                        type="password"
                        className="plain-button2 w-100 border rounded p-2"
                        placeholder="password"
                        value={referencenetworkfields.networkpassword}
                        onChange={(e) => setreferencenetworkfields((prev) => ({ ...prev, networkpassword: e.target.value }))}
                      />
                    </div>
                    <div className="pt-2">
                      <label htmlFor="network-Host" className="form-label">
                        Data Path
                      </label>
                      <input
                        id="network-datapath"
                        type="text"
                        className="plain-button2 w-100 border rounded p-2"
                        placeholder="Data Path"
                        value={referencenetworkfields.networkpath}
                        onChange={(e) => setreferencenetworkfields((prev) => ({ ...prev, networkpath: e.target.value }))}
                      />
                    </div>
                  </div>}
                  {!references3file && <div>
                    <div className="d-a">
                      <label htmlFor="network-Host" className="form-label">
                        Host
                      </label>
                      &nbsp;
                      <input
                        id="network-Host"
                        type="text"
                        className="plain-button2 w-100 border rounded p-2"
                        placeholder="e.g. 192.168.1.100"
                        value={networkfields.networkhost}
                        onChange={(e) => setnetworkfields((prev) => ({ ...prev, networkhost: e.target.value }))}
                      />
                      &nbsp;&nbsp;
                      <label htmlFor="network-port" className="form-label">
                        Port
                      </label>&nbsp;&nbsp;
                      <input
                        id="network-port"
                        type="number"
                        className="plain-button2 w-100 border rounded p-2"
                        placeholder="e.g. 8080"
                        value={networkfields.networkport}
                        onChange={(e) => setnetworkfields((prev) => ({ ...prev, networkport: e.target.value }))}
                      />
                    </div>
                    <div className="d-a pt-2">

                      <input
                        id="network-user"
                        type="text"
                        className="plain-button2 w-100 border rounded p-2"
                        placeholder="User"
                        value={networkfields.networkuser}
                        onChange={(e) => setnetworkfields((prev) => ({ ...prev, networkuser: e.target.value }))}
                      />&nbsp;&nbsp;

                      <input
                        id="network-password"
                        type="password"
                        className="plain-button2 w-100 border rounded p-2"
                        placeholder="password"
                        value={networkfields.networkpassword}
                        onChange={(e) => setnetworkfields((prev) => ({ ...prev, networkpassword: e.target.value }))}
                      />
                    </div>
                    <div className="pt-2">
                      <label htmlFor="network-Host" className="form-label">
                        Data Path
                      </label>
                      <input
                        id="network-datapath"
                        type="text"
                        className="plain-button2 w-100 border rounded p-2"
                        placeholder="Data Path"
                        value={networkfields.networkpath}
                        onChange={(e) => setnetworkfields((prev) => ({ ...prev, networkpath: e.target.value }))}
                      />
                    </div>
                  </div>}
                </div>
              )}
            </section>

            {embedded && (
              <section className="cv-embedded-runtime-toolbar" aria-label="Evaluation runtime settings">
                {selectedTool === "stream" && streamSnapshots.length > 0 && (
                  <div className="cv-embedded-stream-snapshots" aria-label="Captured snapshots">
                    <div className="cv-embedded-stream-snapshots__row">
                      {streamSnapshots.map((snapshot) => (
                        <div key={snapshot.id} className="cv-embedded-stream-snapshot">
                          <button
                            type="button"
                            className="cv-embedded-stream-snapshot__remove"
                            onClick={() => handleRemoveStreamSnapshot(snapshot.id)}
                            aria-label={`Remove ${snapshot.name}`}
                          >
                            ×
                          </button>
                          <img
                            src={snapshot.url}
                            alt={snapshot.name}
                            className="cv-embedded-stream-snapshot__image"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="cv-embedded-runtime-toolbar-row">
                  <div className="cv-embedded-runtime-controls">
                    <EvaluationTargetHardwareMenu
                      devices={data?.devices}
                      selectedTargetHardware={selectedTargetHardware}
                      onTargetHardwareChange={setSelectedTargetHardware}
                      selectedFamily={selectedFamily}
                      onFamilyChange={setSelectedFamily}
                      selectedChipset={selectedChipset}
                      onChipsetChange={setSelectedChipset}
                      selectedDevice={selectedDevice}
                      onDeviceChange={setSelectedDevice}
                      getDeviceStatus={getDeviceStatus}
                      disabled={loadButton}
                      familyRef={familyRef}
                      chipsetRef={chipsetRef}
                      panelPlacement="above"
                      panelAlign="left"
                      useDropdownSelections
                      centerHardwareFamily
                      triggerId="cv-embedded-target-hardware-trigger"
                    />

                    <div
                      className={`cv-embedded-runtime-select cv-embedded-annotation-select cv-embedded-annotation-slot${
                        datatype === "Dataset" ? " is-visible" : ""
                      }`}
                      aria-hidden={datatype !== "Dataset"}
                    >
                      <label className="visually-hidden" htmlFor="cv-annotation-format-select">
                        Annotation format
                      </label>
                      <Select
                        inputId="cv-annotation-format-select"
                        className="eval-target-hardware-panel__react-select cv-annotation-format-react-select"
                        classNamePrefix="eval-target-device-select"
                        options={annotationFormatOptions.filter((option) => !option.isDisabled)}
                        value={
                          annotationFormatOptions.find(
                            (option) => option.value === format
                          ) || null
                        }
                        placeholder="Annotation Format"
                        isDisabled={datatype !== "Dataset"}
                        isSearchable={false}
                        menuPlacement="auto"
                        menuShouldScrollIntoView={false}
                        components={ANNOTATION_SELECT_COMPONENTS}
                        onChange={(option) => {
                          setFormat(option?.value || "");
                          clearTestMedia();
                        }}
                        aria-label="Annotation format"
                      />
                    </div>
                  </div>

                  <Button
                    type="button"
                    className={`cv-embedded-start-button p-1 ps-2 cursor-pointer ${loadButton ? "busy-border" : ""}`}
                    onClick={handleStartEvaluation}
                    disabled={loadButton}
                    aria-label="Start evaluation"
                    title="Start Evaluation"
                  >
                    {loadButton ? (
                      <FontAwesomeIcon icon={faSpinner} spin />
                    ) : (
                      <SendMessageIcon className="wf-icon" style={{ width: "23px", height: "25px" }} />
                    )}
                  </Button>
                </div>
              </section>
            )}

          </div>

          {embedded && datatype === "Dataset" && (
            <section className="cv-embedded-benchmark-section" aria-label="Benchmark datasets">
              <div className="cv-embedded-benchmark-section__header">
                <div>
                  <strong>Benchmark Dataset</strong>
                  <small>Curated benchmark datasets will be available in a future update.</small>
                </div>
                <span>Coming soon</span>
              </div>
              <div className="cv-embedded-benchmark-examples">
                {suggestedImageOptions.map((suggestedImage) => (
                  <button
                    key={suggestedImage.name}
                    type="button"
                    className="llm-example-card llm-example-card-image"
                    disabled
                    aria-disabled="true"
                    title={`${suggestedImage.label} is unavailable for benchmark datasets`}
                  >
                    <img
                      src={suggestedImage.src}
                      alt={suggestedImage.label}
                      loading="lazy"
                      decoding="async"
                    />
                  </button>
                ))}
              </div>
            </section>
          )}

          {embedded && (
            <section
              className={`llm-example-gallery llm-example-gallery-offset cv-embedded-example-gallery${
                datatype === "Dataset" ? " is-placeholder" : ""
              }`}
              aria-label="Sample images"
              aria-hidden={datatype === "Dataset"}
            >
              {datatype !== "Dataset" &&
                suggestedImageOptions.length > 0 && (
                <div className="llm-example-gallery-slide is-enter">
                  <div className="llm-example-gallery-header">
                    <span className="llm-example-gallery-title">
                      <FontAwesomeIcon icon={faWandMagicSparkles} />
                      <span className="llm-example-gallery-sentence">
                        Try with these sample <strong>Image</strong> examples.....
                      </span>
                    </span>
                  </div>
                  <div className="llm-example-gallery-row-wrap llm-example-gallery-row-wrap--images">
                    <div className="llm-example-gallery-row llm-example-gallery-row--images">
                      {suggestedImageOptions.map((suggestedImage) => {
                        const activeSuggestedImages =
                          data?.info?.task_type === "FaceRecognition" && references3file
                            ? selectedrefernceImage
                            : selectedImages;
                        const isLoading = selectingSuggestedImage === suggestedImage.name;
                        const isSelected =
                          isLoading || activeSuggestedImages.some(
                            (image) => image?.name === suggestedImage.name
                          );

                        return (
                          <button
                            key={suggestedImage.name}
                            type="button"
                            className={`llm-example-card llm-example-card-image${
                              isSelected ? " selected" : ""
                            }${isLoading ? " is-loading" : ""}`}
                            onClick={(event) =>
                              handleSuggestedImageSelect(
                                suggestedImage,
                                "",
                                event.currentTarget.querySelector("img")
                              )
                            }
                            disabled={
                              loadButton ||
                              Boolean(selectingSuggestedImage) ||
                              isProcessingFiles ||
                              isProcessingZip
                            }
                            aria-pressed={isSelected}
                            title={`${isSelected ? "Remove" : "Select"} ${suggestedImage.label}${
                              isLoading ? " (loading)" : ""
                            }`}
                          >
                            <img
                              src={suggestedImage.src}
                              alt={suggestedImage.label}
                              loading="lazy"
                              decoding="async"
                            />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </section>
          )}



          {/* FOOTER BUTTONS */}
          <section
            className={`d-flex justify-content-between align-items-center eval-form-footer ${embedded ? "cv-embedded-footer" : "px-3"}`}
            style={embedded ? undefined : { borderTop: '1px solid var(--glass-color)', background: 'var(--bg-color1)', height: '60px', flex: '0 0 60px' }}
          >
            <div></div>

            <button
              ref={startButtonRef}
              type="button"
              disabled={loadButton}
              onClick={handleStartEvaluation}

              className="btn btn-primary px-4 d-flex align-items-center gap-2"
            >
              {loadButton ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faPlay} />}
              Start Evaluation
            </button>
          </section>
          {/* <div className="mobile-view-bottom"></div> */}
        </form>

        <Popover
          key={infoPopover.target}
          isOpen={infoPopover.open}
          target={infoPopover.target}
          placement="top"
          toggle={hideInfoPopover}
        >
          <PopoverBody className={infoPopover.type === "danger" ? "text-danger fw-semibold" : ""}>{infoPopover.message}</PopoverBody>
        </Popover>
        {/* <div className="mobile-view-bottom"></div> */}
      </>
    );
  };

  export default CvEvaluationField;
